/**
 * inPerson.ts — finishing a deal face to face, on the website (card
 * 1218886635703438; Blake's signed-off handoff and COPY.txt of 2026-09-28).
 *
 * Every server call the in-person screens make lives here: start, resolve,
 * status, stop, the second person's confirm, the offer read that fills the
 * confirm screen, and the receipt read. app.js paints one state object
 * (`inPerson`, declared there) and handles only pure UI taps; this module
 * moves that state through the bridge (`__gbzInPersonSet`) and never invents
 * a word about money: a refusal shows the server's own sentence, a decline
 * shows Stripe's, and nothing reads as complete until the server says so.
 *
 * THE CONTRACT THIS SERVES (origin/nick/in-person-handoff @ 0d3eb262,
 * artifacts/api-server/src/routes/offersInPerson.ts, all requireAuth, rate
 * limited per user: 60 per 15 min for the writes, 1,200 for status):
 *
 *   POST /api/offers/:id/in-person/start            — the FIRST person
 *     → { ok, code, expiresAt, feeCents, clientSecret|null, waitingFor }
 *     409 theirsAlready when the other side went first: scan their code.
 *   GET  /api/offers/in-person/resolve/:code        — the scanner lands
 *     → { ok, offerId, role, feeCents, expiresAt, otherSideConfirmed: true }
 *   POST /api/offers/:id/in-person/authorize-fee    — the SECOND person's $1
 *     → { ok, feeCents, clientSecret|null, alreadyAuthorized? }
 *       (feeCents 0 and no secret for a seller on a purchase; one hold per
 *       person, returned again while it is still usable)
 *       410 the code ran out, 409 notStarted when the other side stopped:
 *       the same two screens as confirm's (HandoffEndedError).
 *   POST /api/offers/:id/in-person/confirm          — the SECOND confirm
 *     → { ok, completed: true, pointsAwarded, feeCents, feeCaptured }
 *       402 "Finish your payment on your phone first, then confirm." (mine)
 *       409 "They haven't finished paying yet…" (theirs, code feeNotAuthorizedTheirs); 410 the code ran
 *       out between the scan and the tap; 409 notStarted when the other side
 *       stopped. It reads no body; the code went through resolve.
 *   GET  /api/offers/:id/in-person/status
 *     → { ok, startedInPerson, yours, theirs, completed, code|null, expiresAt|null }
 *       (code: the live code, to the phone that started it only)
 *   POST /api/offers/:id/in-person/stop
 *     → { ok, stopped: true }
 *
 * ⚠️ NONE OF THE WRITES HONORS THE Idempotency-Key HEADER YET (no middleware
 * on the routes). It is sent anyway; a retried start whose first attempt
 * landed is refused with 409 and read against status instead.
 *
 * 🔴 THE PAYMENT ELEMENT IS inPersonPay.ts (card 1218526698505993), installed
 * by WebInstall.tsx before this module when the build carries a publishable
 * key (stripeJs.ts; Tyler's ruling N1 bakes VITE_STRIPE_PUBLISHABLE_KEY into
 * the build). Without a key nothing fills the hook and the honest disabled
 * state below stands. The screens carry the slot and this hook contract; the
 * element drops into it:
 *
 *   window.__gbzInPersonPay = {
 *     mount(slot: HTMLElement, opts: {
 *       feeCents: number,
 *       // Asks this module for the PaymentIntent (it calls start or confirm
 *       // on the server) and resolves its client secret.
 *       intent: () => Promise<string>,
 *       onReady: (info: { wallet: boolean }) => void,   // Apple/Google Pay available?
 *       onPaid: () => void,                             // the intent is authorized
 *       onError: (message: string) => void,             // Stripe's own sentence (E3)
 *       onLoadFailed?: () => void,                      // the form will not load (IP4a H5)
 *     }): () => void,                                    // unmount
 *     // Card mode: confirm the intent with the card in the element.
 *     confirmCard(): Promise<void>,
 *     // "Try another card": clear the element.
 *     reset(): void,
 *     // Optional: the card is complete, before a first payer's start.
 *     validate?(): Promise<void>,
 *     // Optional (IP4b, M8): after an unknown outcome, the intent read back:
 *     // "held" or "unpaid"; throws when Stripe can't be reached.
 *     check?(clientSecret: string): Promise<"held" | "unpaid">,
 *   }
 *
 * A payer's button enables only when the element calls onReady. Until then it
 * stays disabled under app.js's quiet loading line; a form that will not load
 * (onLoadFailed, or not ready PAY_FORM_TIMEOUT_MS after mounting) gets
 * PAY_FORM_FAILED and a Try again that remounts it, in place of the button
 * (IP4a H5). With no hook at all it stays disabled with PAY_NOT_READY.
 *
 * Without the hook a payer's confirm button is DISABLED and the slot says so
 * (`PAY_NOT_READY`), rather than recording a confirm whose $1 could never be
 * authorized. A seller on a purchase owes nothing and is never gated by it.
 *
 * The element lives in ONE persistent host node (`.ip-pay-host`) that NEVER
 * LEAVES THE DOCUMENT: it hangs off <body>, positioned over the slot, and is
 * re-placed after every repaint of the confirm screen (a detached iframe loses
 * its browsing context, so moving the host through app.js's whole-page
 * repaints would wipe a card mid-entry). mount() runs once per screen;
 * unmount runs when the screen is cleared or leaves the confirm phase.
 *
 * WHY THE OFFER READ. The deal row (`mapDealThread`) carries neither the offer
 * type nor the counterpart's avatar/tier; GET /api/offers/:id returns both
 * users, the listing, the offered and requested items and the type, and it is the read
 * exchangePrefill.ts already makes. The confirm screen's rows and the fee
 * question (buyer always; seller unless cash/coin_flip — the server's own
 * feePayers rule, mirrored here for DISPLAY only, `feeCents` from the server
 * is what the code screen and receipt print) come from it.
 *
 * E1 AND THE RELOAD. Live sign-in ends in a page reload onto /#/profile
 * (authSeam.tsx), so a scanned link cannot survive it in memory. app.js writes
 * `sessionStorage[RETURN_KEY]` when it paints E1 for a signed-out scanner;
 * this module, installed for the signed-in visitor after that reload, reads
 * the key once, clears it and navigates there. Session storage, not local:
 * it belongs to this tab and dies with it.
 *
 * E4 AND IDEMPOTENCY. A confirm that fails to reach the server is kept and
 * retried every RETRY_MS with the SAME `Idempotency-Key` header, so a request
 * that did land is never doubled. Nothing shows as complete until an answer
 * arrives. Since IP4b every request gives up after REQUEST_TIMEOUT_MS (a write
 * then goes to E4 too), E4 says only that it waits for a connection, and its
 * Stop for now stays tappable.
 *
 * THE CODE. `qrcode` (node-qrcode) builds the matrix; the SVG is one path so
 * the card stays crisp at any size. Encodes `<origin>/h/<CODE>`, which lands
 * on the api-server (Nick's branch adds `/h` to artifact.toml's paths) and
 * from there on `/#/h/<CODE>`.
 */

import QRCode from "qrcode";
import { installDealThread, refreshDeals } from "./floorTransport";
import { LONG_TOAST_MS } from "./wantsWrite";

/** 01, the deal page: the button beside Check out. Its value is the thread id. */
export const IN_PERSON_SELECTOR = "[data-inperson]";
/** The screens' write controls. Everything else `data-ip` is app.js's own UI. */
export const CONFIRM_SELECTOR = '[data-ip="confirm"]';
export const STOP_SELECTOR = '[data-ip="stop"]';
export const RETRY_SELECTOR = '[data-ip="retry"]';
export const ANOTHER_CARD_SELECTOR = '[data-ip="anothercard"]';
/** Try again on the error sheet (IP4a M4): In person again, or the confirm screen back for another tap. */
export const REOPEN_SELECTOR = '[data-ip="reopen"]';
/** Try again when the card form failed to load (IP4a H5): a fresh payment element. */
export const RELOAD_PAY_SELECTOR = '[data-ip="reloadpay"]';
/** "Have an event code?" on the confirm screen (Bleecker show, 2026-10-03). */
export const EVENT_CODE_SELECTOR = '[data-ip="eventcode"]';
export const EVENT_CODE_PATH = "in-person/event-code";
/** The toast after a redemption that waived a fee (the server's `waived`; IP4a M3, Blake's approved copy of
 *  2026-10-20). A code waives only the flat $1 (in_person_fee), never an item payment. So a hybrid's buyer paying by
 *  card (model "item") hears that only the seller's $1 is covered and that their own charge stands; every other
 *  waiving redemption covers every $1 on the deal. Both stay up LONG_TOAST_MS. */
export const EVENT_CODE_APPLIED = "Event code applied. It covers the $1 in-person fee on this deal.";
export function eventCodeAppliedByCard(sellerHandle: string): string {
  return `Event code applied. It covers @${sellerHandle}'s $1 in-person fee. You still pay the cash part by card, plus the fee and tax.`;
}
/** A code that waived no fee (a card purchase, where it only marks the event, so
 *  the tax is worked out at the event's venue). Blake's approved words of 2026-10-21 ("Approved for event"). */
export const EVENT_CODE_MARKED = "Event code applied. Sales tax is figured for the event's address.";
/** app.js's own close controls (✕ and Back; the backdrops are matched by id on
 *  the target itself, since everything in a sheet is inside its backdrop): this
 *  module clears its clocks and the payment element when the screen they close
 *  is going away. */
export const DISMISS_SELECTOR = "#ip-close";
export const BACKDROP_IDS = ["ip-back"];
/** Back and the receipt's ✕ end the screen whatever it holds. */
export const CLEAR_SELECTOR = '#ipr-close, #ipr-back, [data-ip="back"]';
/** Where the payment element mounts (a payer's confirm screen). */
export const PAY_SLOT_SELECTOR = "[data-ippay]";
/** The countdown text on the code screen, ticked in place. */
export const COUNTDOWN_SELECTOR = "[data-ipcountdown]";

/** The second person's two calls (see the header). */
export const AUTHORIZE_PATH = "in-person/authorize-fee";
export const CONFIRM_PATH = "in-person/confirm";

export const POLL_MS = 2000;
/** Once this phone's countdown has run out: still asking, but not every two seconds. */
export const SPENT_POLL_MS = 10000;
/** The poll's longest wait after the server says slow down (429). */
export const POLL_BACKOFF_MAX_MS = 30000;
export const RETRY_MS = 3000;
export const RETURN_KEY = "gbz_inperson_return";
/** A return older than this (a tab left open, signed in for something else) is dropped. */
export const RETURN_TTL_MS = 15 * 60 * 1000;
export const HANDOFF_ROUTE = /^#\/h\/([A-Za-z0-9]{4,12})$/;

/** Only for a failure the server gave no sentence for. Blake's approved line (IP4a, 2026-10-20): "Try again in a
 *  moment" is the server's own phrasing in its try-again sentences. */
export const FAILURE_MESSAGE = "We couldn't reach the server. Try again in a moment.";
export const SIGNIN_MESSAGE = "Sign in to finish this deal in person";
/** The row carries no offer id yet (dealActions.ts has the same refusal). */
export const UNRESOLVED_OFFER_MESSAGE =
  "Open the deal first — we can't tell which offer this is yet";
/** The honest state of a payer's confirm while the payment element is not on the site. Since IP4a (H5) only a build
 *  with no publishable key says it: with the hook installed the form is loading (app.js's quiet "Loading the card
 *  form..." line) or failed to load (PAY_FORM_FAILED). */
export const PAY_NOT_READY = "Card payment isn't connected on this site yet.";
/** The card form failed to load: Stripe.js did not load, the element fired loaderror, setting it up threw, or it was
 *  not ready PAY_FORM_TIMEOUT_MS after mounting. Also what the hook throws for a tap that reaches it while the form is
 *  not live. Blake's approved line (IP4a H5); app.js prints the same sentence (IP_PAY_FAILED, a test holds them equal)
 *  above a Try again that remounts the form. */
export const PAY_FORM_FAILED = "The card form hasn't loaded. Check your connection, then tap Try again.";
/** How long a mounted card form may stay not ready before it counts as failed (the copy sheet's 20 s). */
export const PAY_FORM_TIMEOUT_MS = 20000;
/** sessionStorage: the deals this tab saw complete in person, per user (IP4a M5 and M7). */
export const DONE_KEY = "gbz_inperson_done";
const DONE_KEEP = 20;
/**
 * 🔴 A CONFIRM WHOSE OUTCOME WE DO NOT KNOW, WHICH IS NOT A REFUSAL.
 * `confirmPayment` failing with `api_connection_error` means the request to
 * Stripe never completed — so the $1 may be AUTHORIZED and we cannot see that
 * it is. Telling this payer what we tell a declined card ("nothing was
 * charged") is a claim we cannot support, and they will see a pending amount on
 * their statement while our screen said it failed. At a venue on shared Wi-Fi
 * this is the likely failure, not the exotic one.
 *
 * Mirrors a ruling the app already made in writing for the item purchase
 * (artifacts/mobile/app/checkout.tsx, the three-situation block): an unknown
 * outcome, an incomplete challenge and a terminal decline must not share one
 * sentence. This is that distinction on the website's $1 fee.
 *
 * 📌 IT OFFERS BOTH WAYS OUT, BECAUSE ONLY ONE OF THEM WORKS IN EACH CASE.
 * Try again re-confirms the SAME clientSecret (inPersonPay.test.ts pins
 * `[FIRST_SECRET, FIRST_SECRET]`), so it can never mint a second authorization,
 * and when the first confirm never reached Stripe it finishes the same $1.
 * But when the first confirm DID go through, the intent is already
 * requires_capture, re-confirming it errors, and the payer would loop on
 * Stripe's sentence. Stop for now releases any hold in both cases. The first
 * draft said Try again "finishes the same $1", which is false in the held case.
 * Agrippa's review of #1000 (for Tyler), wording theirs.
 *
 * Blake's approved line (Asana 1219098137801300), one sentence for the $1 fee
 * and the item payment. It keeps the property the old code got wrong: it does
 * NOT say nothing was charged, because we do not know that.
 *
 * IP4b round 6 (Blake, 2026-10-21, "Good to proceed"): the line is now the one below. It offers Try again alone:
 * since M8, Try again reads the hold back first, and a held intent counts as paid and is never confirmed again, so
 * Try again finishes in both cases. Stop for now stays one tap away under Something's not right?. It still does
 * not say nothing was charged.
 */
export const PAY_UNCONFIRMED =
  "We couldn't reach the card service. Tap Try again to finish. You'll only be charged once.";
/** After a stop the server confirmed (the toast). */
export const STOPPED_MESSAGE = "Stopped for now — nothing was charged";
/** In person tapped on a deal status says is done (the server's own sentence, alreadyComplete). */
export const ALREADY_COMPLETE_MESSAGE = "This deal is already complete.";
/**
 * IP4b (H4c): no in-person request waits longer than this for its answer (Blake's copy sheet, 2026-10-20). The same
 * 20 s the website's other guarded writes use (wantsWrite.ts). A write that runs out goes to E4, whose retry is safe:
 * start reads its 409 against status, authorize-fee hands back the stored hold, and confirm reads status for a
 * completion. The server's own Stripe calls can take far longer, so the number is chosen for the person at the table.
 */
export const REQUEST_TIMEOUT_MS = 20000;
/** IP4b (M9): the second confirm answered 5xx and the deal is not complete. Blake's approved line; a payer also reads
 *  CHARGED_ONCE, since a re-tap reuses the hold. Nothing owed, the first sentence alone. */
export const CONFIRM_RETRY_MESSAGE = "We couldn't finish the deal just now. Try again in a moment.";
export const CHARGED_ONCE = "Your card is only charged once.";

type Role = "buyer" | "seller";
type Kind = "purchase" | "trade" | "hybrid";
type Phase = "confirm" | "code" | "done" | "stopped" | "expired" | "signin" | "resolving" | "offline" | "error" | "complete";

export interface InPersonItem { title: string; image: string | null }
export interface InPersonReceipt {
  ref: string;
  when: string | null;
  points: number | null;
  /** Item mode only: what the server says it captured (confirm's `payment`, or
   *  status's on the first phone). Absent on a $1 deal, and absent when the
   *  server sent nothing readable, in which case the receipt claims no charge. */
  payment?: InPersonPayment;
}
export interface InPersonState {
  threadId: string | null;
  offerId: string | null;
  role: Role;
  kind: Kind;
  with: string;
  withAvatar: string | null;
  youGet: InPersonItem[];
  youGive: InPersonItem[];
  cash: number;
  /** IP6: the cash the seller adds to a trade (offers.requestedCashAmount). No row prints it yet; it only keeps
   *  " You pay nothing." off a waived sheet (app.js ipCashHands). Absent: 0. */
  sellerCash?: number;
  fee: boolean;
  feeCents: number;
  phase: Phase;
  second: boolean;
  fromCode: boolean;
  code: string | null;
  /** The countdown ran out on this phone; the poll goes on and Stop for now is the way out. */
  codeExpired: boolean;
  expiresAt: string | null;
  clientSecret: string | null;
  payReady: boolean;
  payWallet: boolean;
  payMode: "wallet" | "card";
  payErr: string | null;
  /** The server's sentence on a confirm it refused but that can be tried again (402 my fee, 409 their fee). */
  confirmErr: string | null;
  /** IP4b (M8): the hold is being read back after an unknown outcome (app.js: "Checking your payment"). Absent: no. */
  checking?: boolean;
  /** IP4b (H4c): the scanned page got no answer it could read; its Try again reads the code again. Absent: no. */
  reread?: boolean;
  busy: boolean;
  error: string | null;
  hidden: boolean;
  notRight: boolean;
  typing: boolean;
  receipt: InPersonReceipt | null;
  /** The server's quote for THIS person (item mode), or null on today's $1
   *  screens. When it is set, `fee` and `feeCents` are derived from it
   *  (withPayment) and nothing else on the screen names an amount. */
  payment: InPersonPayment | null;
  /** A refusal that gets its own screen (the server's sentence, a note, Back). */
  refusal: Refusal | null;
  /** start or authorize-fee answered with a different amount than the screen
   *  showed: nothing was paid, the new amount is on screen, the next tap pays it. */
  amountChanged: boolean;
  /** IP4a H5: the card form failed to load (absent means it did not). */
  payFailed?: boolean;
  /** IP4a M4: what the error sheet's Try again does ("open" In person again, "confirm" the confirm screen back), or
   *  absent/null for a sheet whose refusal waiting cannot fix (Back to the deal only). */
  retry?: "open" | "confirm" | null;
}

interface Bridge extends Window {
  toast?: (m: string, ms?: number) => void;
  __gbzInPersonCompleted?: (offerId: string) => boolean;
  __gbzInPerson?: () => InPersonState | null;
  __gbzInPersonSet?: (patch: Partial<InPersonState> | null) => boolean;
  __gbzInPersonMark?: (threadId: string, mark: InPersonMark | null) => boolean;
  __gbzInPersonOpen?: (threadId: string) => void;
  __gbzInPersonIntent?: () => Promise<string>;
  __gbzInPersonQrSvg?: (text: string) => string;
  __gbzInPersonKeepsRunning?: (s: InPersonState) => boolean;
  __gbzSelectDealByOffer?: (offerId: string) => boolean;
  __gbzThreadOfferId?: (threadId: string) => string | null;
  __gbzDisputeContext?: (threadId: string) => { offerId: string | null } | null;
  __gbzThreadIsBuyer?: (threadId: string) => boolean | null;
  __gbzInPersonPay?: PayHook;
}

interface WakeSentinel { release: () => Promise<void>; addEventListener?: (t: "release", fn: () => void) => void }

/** The E1 key: "#/h/CODE|<ms>". Followed only while fresh. */
export function parseReturn(value: string | null, nowMs: number): string | null {
  if (!value) return null;
  const [hash, at] = value.split("|");
  if (!HANDOFF_ROUTE.test(hash)) return null;
  const t = Number(at);
  if (!Number.isFinite(t) || nowMs - t > RETURN_TTL_MS) return null;
  return hash;
}

export interface PayHook {
  mount(
    slot: HTMLElement,
    opts: {
      feeCents: number;
      intent: () => Promise<string>;
      onReady: (info: { wallet: boolean }) => void;
      onPaid: () => void;
      onError: (message: string) => void;
      /** IP4a H5: the form will not load (Stripe.js, loaderror, or setup threw). */
      onLoadFailed?: () => void;
    },
  ): () => void;
  confirmCard(): Promise<void>;
  reset(): void;
  /** Optional: the card is complete, checked BEFORE a first payer's start
   *  records their confirm. Throws the element's own sentence. */
  validate?(): Promise<void>;
  /** Optional (IP4b, M8): after an unknown outcome, the intent read back: "held" (the hold is in) or "unpaid".
   *  Throws when Stripe can't be reached. Without it, Try again re-confirms as before. */
  check?(clientSecret: string): Promise<"held" | "unpaid">;
}

interface ApiOffer {
  offer: {
    id: string;
    listingId: string | null;
    fromUserId: string;
    toUserId: string;
    status: string;
    offerType: string | null;
    cashAmount: number | null;
    /* The cash the SELLER adds to balance a trade (offers.requestedCashAmount; GET /:id sends the whole offers row).
       Optional: read only to tell a waived side that cash still changes hands (IP6). */
    requestedCashAmount?: number | null;
  };
  listing: { id: string; title: string; images: string[] | null } | null;
  fromUser: { id: string; username: string; avatar: string | null };
  toUser: { id: string; username: string; avatar: string | null };
  offeredItems: Array<{ name: string; image: string | null }>;
  /* The seller's pieces beyond the anchor listing, in the server's position order
     (offers.ts GET /:id). Optional: a server before the field sends none. */
  requestedItems?: Array<{ name: string; image: string | null; listingId?: string | null }>;
}

/* `payment` on each answer is read ONLY through readPayment, never trusted as typed. */
interface StartResponse { ok: true; code: string; expiresAt: string; feeCents: number; clientSecret: string | null; waitingFor: Role; payment?: unknown }
interface ResolveResponse { ok: true; offerId: string; role: Role; feeCents: number; expiresAt: string; otherSideConfirmed: boolean; payment?: unknown }
interface StatusResponse { ok: true; startedInPerson: boolean; yours: boolean; theirs: boolean; completed: boolean; code?: string | null; expiresAt: string | null; payment?: unknown; yoursHeld?: unknown }
interface ConfirmResponse { ok: true; completed: boolean; pointsAwarded?: number; feeCents?: number; payment?: unknown }
interface AuthorizeResponse { ok: true; feeCents: number; clientSecret: string | null; alreadyAuthorized?: boolean; payment?: unknown }
interface ReceiptResponse { refNum: string; acceptedAt: string | null }

/* ── ITEM MODE: the server's quote (card 1218886635703438) ──────────────────
 *
 * Tyler's 2026-09-29 ruling moved a purchase, and the cash half of a hybrid,
 * INTO the app: under the flag in_person_in_app_payment_enabled the buyer pays
 * item + GoBazaar fee + sales tax by card at the table, through the same
 * manual-capture PaymentIntent as the $1. The server half (#1002) can already
 * charge it. These screens could not say it.
 *
 * THE CONTRACT (proposed to Nick and Ceci on the card on 2026-09-30, NOT yet on
 * the server): resolve, status, start and authorize-fee each carry
 *
 *   payment: { model: "fee" | "item" | "none", itemCents, feeCents, taxCents,
 *              totalCents, taxLabel? }
 *
 * where totalCents is the PaymentIntent's amount to the cent, and confirm
 * carries the same object for what it captured. Two new refusals come with
 * it: overCap and sellerAddsCash.
 *
 * 🔴 THREE RULES, EACH A WAY THIS COULD CHARGE SOMEONE WHAT THE SCREEN DID NOT SAY.
 *
 *  1. ABSENT MEANS TODAY. No `payment` on an answer (today's server, or the
 *     flag off) is the $1 flow exactly: same words, same requests. This module
 *     never reads the flag and never infers the model from the offer.
 *  2. PRESENT BUT UNREADABLE FAILS CLOSED. A payment object that is there but
 *     does not add up is not a $1 deal: the screen says AMOUNT_UNAVAILABLE and
 *     shows no pay button. Falling back to $1 would print one number and let
 *     the server charge another.
 *  3. THE CLIENT NEVER MAKES A NUMBER. Every amount on screen, the button's
 *     and the payment element's included, is a field of `payment`. The only
 *     arithmetic here is the CHECK that the fields add up.
 *
 * 🔑 A QUOTE THAT MOVES BETWEEN THE SCREEN AND THE INTENT IS NOT PAID. status
 * and resolve quote before any intent exists; start and authorize-fee answer
 * with the intent itself. If those disagree (a flag flipped mid-screen, a tax
 * figure that moved), confirmCard is NOT called: the new quote is painted, the
 * element is rebuilt with the new amount, and the person taps again. Paying
 * the old number's button with the new number's intent is the failure this
 * whole block exists to prevent.
 */

export type PaymentModel = "fee" | "item" | "none";
export interface InPersonPayment {
  model: PaymentModel;
  itemCents: number;
  feeCents: number;
  taxCents: number;
  totalCents: number;
  /** The server's name for the tax line ("NYC sales tax"). The screen says
   *  "Sales tax" without it: naming a city the server did not name would be a claim. */
  taxLabel: string | null;
  /** IP5 (Blake, 2026-10-21): the event code that waived this side's $1, which the receipt's fee row names. Asked of
   *  Nick on the waived payment object (offersInPerson.ts quoteFor); kept only when it is EVENT_CODE_SHAPE, and the
   *  key is left off otherwise, so an answer without it reads exactly as before. */
  eventCode?: string;
}
/** An event code as the server spends it: its event-code route keeps only A to Z, 0 to 9 and "-". */
export const EVENT_CODE_SHAPE = /^[A-Z0-9-]{1,40}$/;
/** "taxUnavailable" is never returned by refusalOf: it only carries the house note on In person's sheet for the
 *  server's transient tax_location_unavailable (IP4a M4), which keeps its Try again. */
export type Refusal = "overCap" | "sellerAddsCash" | "payoutBuyer" | "payoutSeller" | "taxLocation" | "taxUnavailable";
/** The server's in-person sentence for the SELLER who cannot be paid by card
 *  (offersInPerson.ts PAYOUT_REFUSAL_SELLER; a test holds them equal). The buyer's
 *  names the seller, so it is told apart by this one. */
export const PAYOUT_REFUSAL_SELLER = "Connect a payout account to get paid by card.";
/** The gate's reasons (lib/sellerPayability.ts), sent as the refusal's code. */
const PAYOUT_CODES = new Set(["seller_no_payout_account", "seller_payouts_disabled"]);
/** S1's mark on a deal row. `payment` only in item mode, so a $1 mark is unchanged.
 *  `unknown`: a side that stands confirmed whose quote status does not send, so
 *  the summary leaves out the sentence that differs between the $1 and a card
 *  payment (a seller on a purchase: the cash, or paid to their account). */
export interface InPersonMark { started: boolean; fee: boolean; payment?: InPersonPayment; unknown?: boolean; kind?: Kind; role?: Role }

/** A payment object that is present but cannot be trusted: the whole screen, no pay button. */
export const AMOUNT_UNAVAILABLE = "We couldn't load the amount. Try again in a moment.";
/** start or authorize-fee named a different amount than the screen showed.
 *  Blake's approved line with the new total; app.js builds the same sentence
 *  (ipAmountChanged), and a test holds them equal. */
export function amountChanged(totalCents: number): string {
  return `The total is now $${(totalCents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} after tax was finalized. Check it, then tap Confirm.`;
}
/** The $1 screens print "$1" in their words. A model "fee" quote for any other
 *  amount would make those words false, so it fails closed instead. The server's
 *  IN_PERSON_FEE_CENTS; inPersonCardPay.test.ts parses it and holds them equal. */
export const FEE_SCREEN_CENTS = 100;

export type PaymentRead = { kind: "absent" } | { kind: "bad" } | { kind: "ok"; payment: InPersonPayment };

/**
 * The `payment` on a server answer: absent, unreadable, or checked.
 *
 * Checked means: a known model; four non-negative whole-cent integers; and
 *   · item: something to pay for, and total = item + fee + tax exactly
 *     (the server's own `amountCents = itemCents + feeCents + taxAmountCents`);
 *   · fee:  the same sum, and it is the $1 the fee screens print;
 *   · none: this person pays nothing (fee, tax and total all 0). itemCents is
 *     what they are paid: 0 for a side an event code waived (and for the seller
 *     on a purchase with no readable price), above 0 for a payee (the seller on
 *     a purchase, or a hybrid's seller with card pay on, who is still paid the
 *     cash part when a code waives their $1). Which of those a side is, is the
 *     screen's call (app.js ipFeeWaived), not this check's.
 * `null` counts as absent, like a missing key.
 */
export function readPayment(body: unknown): PaymentRead {
  if (!body || typeof body !== "object") return { kind: "absent" };
  const raw = (body as { payment?: unknown }).payment;
  if (raw === undefined || raw === null) return { kind: "absent" };
  const bad: PaymentRead = { kind: "bad" };
  if (typeof raw !== "object" || Array.isArray(raw)) return bad;
  const p = raw as Record<string, unknown>;
  const model = p.model;
  if (model !== "fee" && model !== "item" && model !== "none") return bad;
  const cents = (v: unknown): number | null => (typeof v === "number" && Number.isSafeInteger(v) && v >= 0 ? v : null);
  const itemCents = cents(p.itemCents);
  const feeCents = cents(p.feeCents);
  const taxCents = cents(p.taxCents);
  const totalCents = cents(p.totalCents);
  if (itemCents === null || feeCents === null || taxCents === null || totalCents === null) return bad;
  if (model === "none") {
    /* This side pays nothing. itemCents is what it is paid: 0 for a side an
       event code waived, above 0 for a payee (see above). */
    if (totalCents !== 0 || feeCents !== 0 || taxCents !== 0) return bad;
  } else {
    if (totalCents !== itemCents + feeCents + taxCents) return bad;
    if (model === "item" && itemCents <= 0) return bad;
    if (model === "fee" && totalCents !== FEE_SCREEN_CENTS) return bad;
  }
  const taxLabel = typeof p.taxLabel === "string" && p.taxLabel.trim() ? p.taxLabel.trim() : null;
  /* IP5: carried through deliberately (this rebuild drops every key it does not name), as sent, never trimmed. */
  const eventCode = typeof p.eventCode === "string" && EVENT_CODE_SHAPE.test(p.eventCode) ? { eventCode: p.eventCode } : {};
  return { kind: "ok", payment: { model, itemCents, feeCents, taxCents, totalCents, taxLabel, ...eventCode } };
}

/**
 * The refusals that get their own screen. By the server's `code` when it sends
 * one (asked of Nick: `refuse()` sends only `error` today), else by the
 * contract's sentence, since overCap's sentence carries the deal's own total
 * and cannot be matched whole. The sentence shown is always the server's.
 */
export function refusalOf(body: unknown): Refusal | null {
  if (!body || typeof body !== "object") return null;
  const b = body as { code?: unknown; error?: unknown };
  if (b.code === "overCap" || b.code === "sellerAddsCash") return b.code;
  if (b.code === "tax_location_missing") return "taxLocation";
  const e = typeof b.error === "string" ? b.error : "";
  if (typeof b.code === "string" && PAYOUT_CODES.has(b.code)) return e === PAYOUT_REFUSAL_SELLER ? "payoutSeller" : "payoutBuyer";
  if (/^Card payments in person are capped at /.test(e)) return "overCap";
  if (/^In person doesn['\u2019]t cover a trade where the seller adds cash/.test(e)) return "sellerAddsCash";
  return null;
}

/** IP4a M4's button rule for a refusal without its own screen: Try again when waiting can help (the sentence says
 *  try again, or the answer was a 5xx, a 429 or no answer at all, status 0), Back to the deal only otherwise. */
export function retryHelps(httpStatus: number, sentence: string): boolean {
  return httpStatus === 0 || httpStatus === 429 || httpStatus >= 500 || /\btry again\b/i.test(sentence);
}

/** A refusal with its own screen, thrown out of authorizeFee. */
class RefusedError extends Error {
  constructor(message: string, readonly refusal: Refusal) { super(message); }
}
/** authorize-fee refused because the handoff is over: the code ran out (410),
 *  or the other side stopped (409 notStarted). Thrown out of authorizeFee so the
 *  caller shows the screen confirm's same refusal shows (confirmSecond), not
 *  the confirm screen with a button that would only be refused again. */
class HandoffEndedError extends Error {
  constructor(message: string, readonly phase: "expired" | "stopped") { super(message); }
}
/** An answer whose payment could not be trusted, thrown out of authorizeFee. */
class AmountError extends Error {
  constructor() { super(AMOUNT_UNAVAILABLE); }
}

/** The state a checked quote sets. `feeCents` stays "the intent's amount", which
 *  is what the payment element is mounted with, so it is the quote's TOTAL. */
function withPayment(p: InPersonPayment): Pick<InPersonState, "payment" | "fee" | "feeCents"> {
  return { payment: p, fee: p.model !== "none", feeCents: p.totalCents };
}

/** Does this answer's quote differ from what the screen is showing? A $1
 *  screen (no quote) agrees only with the same $1, or with nothing owed on a
 *  screen that owed nothing. */
function quoteChanged(s: InPersonState, p: InPersonPayment): boolean {
  const q = s.payment;
  if (q) {
    return q.model !== p.model || q.itemCents !== p.itemCents || q.feeCents !== p.feeCents
      || q.taxCents !== p.taxCents || q.totalCents !== p.totalCents;
  }
  if (p.model === "item") return true;
  if (p.model === "none") return s.fee;
  return !s.fee || p.totalCents !== s.feeCents;
}

/* IP5: a mark whose payment is "none" also carries this handoff's kind and role (from the offer's type, as the
   receipt and the confirm screen read them), so the summary's waived row follows the same rule (app.js
   ipFeeWaived). Every other mark is unchanged. */
function markOf(s: Pick<InPersonState, "fee" | "payment" | "kind" | "role">): InPersonMark {
  if (!s.payment) return { started: true, fee: s.fee };
  return s.payment.model === "none"
    ? { started: true, fee: s.fee, payment: s.payment, kind: s.kind, role: s.role }
    : { started: true, fee: s.fee, payment: s.payment };
}

/** The mark for a deal whose amount could not be read: started, and no fee row
 *  or total on the summary, so it never names a figure the screen refused. */
function unreadableMark(): InPersonMark {
  return { started: true, fee: false };
}

/** The mark for a waiting screen (a side that stands confirmed) whose status
 *  carries no quote: status quotes only a side that has not confirmed, so the
 *  summary names no amount (a $1 row would be false over a card payment) and,
 *  `unknown`, no sentence that depends on which it is. */
function unknownMark(): InPersonMark {
  return { started: true, fee: false, unknown: true };
}

/** An answer WITHOUT `payment` is a $1 screen's, and those screens print "$1"
 *  or nothing. Any other amount there (the in-app flag flipped before the
 *  server sends `payment`, so start's intent is item + fee + tax) would be
 *  charged under a "Pay $1" button: it fails closed like an unreadable quote.
 *  The flag-off server only ever sends 0 or 100, so today's flow is untouched. */
function isScreenFee(cents: unknown): boolean {
  return cents === 0 || cents === FEE_SCREEN_CENTS;
}

/** The server's feePayers rule, mirrored for the confirm screen's DISPLAY. */
export function feePayers(offerType: string | null): { buyer: boolean; seller: boolean } {
  const isPurchase = offerType === "cash" || offerType === "coin_flip";
  return { buyer: true, seller: !isPurchase };
}

export function kindOf(offerType: string | null): Kind {
  if (offerType === "cash" || offerType === "coin_flip") return "purchase";
  return offerType === "hybrid" ? "hybrid" : "trade";
}

/** The white card's code, as one SVG path. Modules only; the quiet zone is CSS padding. */
export function qrSvg(text: string): string {
  const q = QRCode.create(text, { errorCorrectionLevel: "M" });
  const n = q.modules.size;
  let d = "";
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (q.modules.get(r, c)) d += `M${c} ${r}h1v1h-1z`;
    }
  }
  return `<svg viewBox="0 0 ${n} ${n}" shape-rendering="crispEdges" role="img" aria-label="Code to scan"><path d="${d}"/></svg>`;
}

export function formatCountdown(msLeft: number): string {
  const s = Math.max(0, Math.ceil(msLeft / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function idempotencyKey(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return "ip-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

/** IP4b (H4c): what a request with no answer after REQUEST_TIMEOUT_MS rejects with. To the person it is a lost
 *  connection, so isNetworkFailure reads it as one (a write goes to E4), and anything that prints its message prints
 *  the shared failure line. */
export class RequestTimeout extends Error {
  constructor() { super(FAILURE_MESSAGE); this.name = "TimeoutError"; }
}

function isNetworkFailure(err: unknown): boolean {
  return err instanceof TypeError || err instanceof RequestTimeout || (typeof err === "object" && err !== null && (err as { name?: string }).name === "TypeError");
}

type Fetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** IP4b (H4c): `p`, or a RequestTimeout once REQUEST_TIMEOUT_MS pass first (calling `onTimeout` before it rejects).
 *  The clock is the module's injected one, so tests move it by hand. */
function withinTimeout<T>(
  p: Promise<T>,
  setTimer: (fn: () => void, ms: number) => number,
  clearTimer: (id: number) => void,
  onTimeout?: () => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const id = setTimer(() => {
      if (settled) return;
      settled = true;
      try { onTimeout?.(); } catch { /* nothing left to cancel */ }
      reject(new RequestTimeout());
    }, REQUEST_TIMEOUT_MS);
    p.then(
      (v) => { if (settled) return; settled = true; clearTimer(id); resolve(v); },
      (e: unknown) => { if (settled) return; settled = true; clearTimer(id); reject(e); },
    );
  });
}

/** IP4b (H4c): the seam's fetch with REQUEST_TIMEOUT_MS on it. Until today no in-person request had a timeout, so a
 *  captive portal or a stalled connection left a disabled button with no message until the browser gave up, and a
 *  hung status poll stopped the code screen asking at all (its next poll is set only after the await). The clock
 *  covers the whole answer, its body included (round 3, the adversarial MINOR-2: a body that stalled after its
 *  headers left the poll waiting for good), so no caller's res.json() can hang. When it runs out the request is also
 *  aborted, so the browser stops waiting for it. */
function timedFetch(fetchImpl: Fetch, setTimer: (fn: () => void, ms: number) => number, clearTimer: (id: number) => void): Fetch {
  return (input, init) => {
    const ctl = new AbortController();
    let p: Promise<Response>;
    try { p = fetchImpl(input, { ...init, signal: ctl.signal }); } catch (err) { return Promise.reject(err); }
    return withinTimeout(p.then(readWholeBody), setTimer, clearTimer, () => ctl.abort());
  };
}

/** Reads an answer's body now, and hands the answer back with json() (and text()) answering from what was read: the
 *  same value, or the same error a bad body throws. A Response-like that has only json() (the tests' stand-ins) gets
 *  its json() read now the same way. */
async function readWholeBody(r: Response): Promise<Response> {
  const body = r as unknown as { json: () => Promise<unknown>; text?: () => Promise<string> };
  if (typeof body.text === "function") {
    const text = await body.text();
    body.text = async () => text;
    body.json = async () => JSON.parse(text) as unknown;
    return r;
  }
  let read: { value: unknown } | { error: unknown };
  try { read = { value: await body.json() }; } catch (error) { read = { error }; }
  body.json = async () => {
    if ("error" in read) throw read.error;
    return read.value;
  };
  return r;
}

export function installInPerson(
  win: Window,
  untimedFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId: string,
  {
    notify = (message: string, ms?: number) => {
      if (!win.document.getElementById("toast")) return;
      (win as Bridge).toast?.(message, ms);
    },
    now = () => Date.now(),
    setTimer = (fn: () => void, ms: number) => win.setTimeout(fn, ms),
    clearTimer = (id: number) => win.clearTimeout(id),
  }: {
    notify?: (m: string, ms?: number) => void;
    now?: () => number;
    setTimer?: (fn: () => void, ms: number) => number;
    clearTimer?: (id: number) => void;
  } = {},
): () => void {
  const w = win as Bridge;
  /* IP4b (H4c): 20 s on every request this module makes. One wrapper at the seam's own fetch, so every read, every
     write (post is built on it) and the deal list's re-read after them are covered without touching a call site. */
  const authFetch = timedFetch(untimedFetch, setTimer, clearTimer);
  const state = (): InPersonState | null => {
    try { return w.__gbzInPerson?.() ?? null; } catch { return null; }
  };
  const set = (patch: Partial<InPersonState> | null): void => {
    try { w.__gbzInPersonSet?.(patch); } catch { /* the bridge is not there yet */ }
  };
  const mark = (threadId: string | null, m: InPersonMark | null): void => {
    if (!threadId) return;
    try { w.__gbzInPersonMark?.(threadId, m); } catch { /* same */ }
  };

  /* -- what this tab saw complete in person (IP4a M5, M7) --------------------
     Confirm nulls the code, so resolve answers codeUnknown for any spent code;
     and nothing the website reads says a deal was finished in person
     (receipts.ts and the deal list carry no marker, and inPersonStartedAt
     survives a stop, so a stopped-then-shipped deal has it too). The only thing
     that knows is the tab that watched the server say "completed" on an
     in-person screen. finish() writes {offerId, code} here, in this tab's
     sessionStorage, keyed by the signed-in user so another account in the tab
     never reads it. A deal finished on another phone, or in a closed tab, is
     not here, and its screens keep today's words. */
  const doneKey = `${DONE_KEY}:${myUserId}`;
  function doneList(): Array<{ offerId: string; code: string | null }> {
    try {
      const raw: unknown = JSON.parse(win.sessionStorage.getItem(doneKey) ?? "[]");
      if (!Array.isArray(raw)) return [];
      return raw.filter((e): e is { offerId: string; code: string | null } =>
        !!e && typeof e === "object" && typeof (e as { offerId?: unknown }).offerId === "string"
          && ((e as { code?: unknown }).code === null || typeof (e as { code?: unknown }).code === "string"));
    } catch { return []; }
  }
  function rememberDone(offerId: string | null, code: string | null): void {
    if (!offerId) return;
    const list = doneList().filter((e) => !(e.offerId === offerId && e.code === code));
    list.push({ offerId, code });
    try { win.sessionStorage.setItem(doneKey, JSON.stringify(list.slice(-DONE_KEEP))); } catch { /* private mode: the screens keep today's words */ }
  }
  const finishedHere = (code: string) => doneList().find((e) => e.code === code) ?? null;

  /* -- a toast that must be read (IP4a M3) ---------------------------------
     It stays up LONG_TOAST_MS (wantsWrite.ts's precedent, 6 s). app.js rebuilds
     #toast on every paint, and the reopen after a redemption paints at once,
     so the sentence is written again into a NEW, EMPTY toast node until its
     time is up, and only while the node it was last written into still holds
     it (a newer toast wins). checkoutContext.ts does the same for its own. */
  let longToast: { message: string; at: number; node: HTMLElement } | null = null;
  function showLong(message: string): void {
    notify(message, LONG_TOAST_MS);
    const node = win.document.getElementById("toast");
    longToast = node && node.textContent === message ? { message, at: now(), node } : null;
  }
  function reshowLong(): void {
    const t = longToast;
    if (!t) return;
    const left = LONG_TOAST_MS - (now() - t.at);
    if (left <= 0 || t.node.textContent !== t.message) { longToast = null; return; }
    /* Only while an in-person screen is up: a closed sheet or another page ends it (adversarial pass 1 m-4). The
       redemption's own reopen clears the screen before its reads land (open() sets `opening` first): that gap is not
       the person leaving. */
    const s = state();
    if (!s && opening) return;
    if (!s || s.hidden) { longToast = null; return; }
    const node = win.document.getElementById("toast");
    if (!node || node === t.node) return;
    /* Another toast already took the fresh node (this module's or anyone's): it wins, for good (m-4). */
    if (node.textContent) { longToast = null; return; }
    notify(t.message, left);
    longToast = node.textContent === t.message ? { ...t, node } : null;
  }

  /* ── the generation ────────────────────────────────────────────────────────
     Every screen has a generation number, bumped whenever the screen is
     cleared or replaced (✕ off a screen with nothing on the server, Back, a
     stop, a new In person tap, a new code on the route). Every continuation
     that comes back from an await captures the generation it started under
     and paints nothing if it has moved on. A screen the ✕ only HIDES keeps its
     generation: an answer that lands after it paints into the hidden screen,
     and the receipt shows itself. */
  let gen = 0;
  const live = (g: number): boolean => g === gen;

  let pollTimer: number | null = null;
  let tickTimer: number | null = null;
  let retryTimer: number | null = null;
  let wakeLock: WakeSentinel | null = null;
  let wakeRequesting = false;
  let opening = false;
  let stopping = false;
  /* One intent is paid once per screen, whichever signal says so first
     (confirmCard resolving, or the element's own onPaid). */
  const paidHandled = new Set<string>();
  /* The codes this page showed (H3, payers; the second review's m-1): offerId ->
     the expiresAt of a live code this page itself put on screen (startClocks).
     In memory only, in this install's closure: a reload forgets it, and so does
     a sign-out (signOutAndReload reloads the page). Never in storage. */
  const shown = new Map<string, string>();

  /** Everything that must stop when the screen is cleared or replaced. */
  function reset(): void {
    stopClocks();
    unmountPayHost();
    pendingPaid = null;
    parked = null;
    stoppedOnE4 = false;
    settling = false;
    if (settleTimer !== null) { clearTimer(settleTimer); settleTimer = null; }
    paidHandled.clear();
    gen++;
  }

  /* ── the code screen's clocks ─────────────────────────────────────────── */

  function stopClocks(): void {
    if (pollTimer !== null) { clearTimer(pollTimer); pollTimer = null; }
    if (tickTimer !== null) { clearTimer(tickTimer); tickTimer = null; }
    if (retryTimer !== null) { clearTimer(retryTimer); retryTimer = null; }
    releaseWakeLock();
  }

  /** How long the code at `expiresAt` has left on this phone's clock. The
   *  countdown (tick) and a code restored from status (liveCodeOf) both read
   *  it, so the two can never disagree about whether a code is spent. */
  function msLeft(expiresAt: string): number {
    return Date.parse(expiresAt) - now();
  }

  function tick(): void {
    tickTimer = null;
    const s = state();
    if (!s || s.phase !== "code" || !s.expiresAt || s.codeExpired) return;
    const left = msLeft(s.expiresAt);
    const el = win.document.querySelector(COUNTDOWN_SELECTOR);
    if (el) el.textContent = formatCountdown(left);
    if (left <= 0) {
      /* The countdown ends on this phone's clock, so the screen says the code
         is spent and offers Stop for now — but it keeps ASKING THE SERVER, more
         slowly and without holding the screen awake: a fast phone clock must
         not hide a completion that landed. */
      set({ codeExpired: true, code: null });
      releaseWakeLock();
      if (s.offerId) shown.delete(s.offerId);
      return;
    }
    tickTimer = setTimer(tick, 1000);
  }

  async function poll(): Promise<void> {
    pollTimer = null;
    const s = state();
    if (!s || s.phase !== "code" || !s.offerId) return;
    const g = gen;
    const offerId = s.offerId;
    try {
      const res = await authFetch(`/api/offers/${encodeURIComponent(offerId)}/in-person/status`);
      if (!live(g)) return;
      if (res.ok) {
        const st = (await res.json()) as StatusResponse;
        const cur = state();
        if (!live(g) || !cur || cur.phase !== "code" || cur.offerId !== offerId) return;
        if (st.completed) {
          await finish(cur, null, g, readPayment(st));
          return;
        }
        if (cur.expiresAt && !cur.codeExpired && st.expiresAt === null && !st.theirs) {
          /* The code is gone while this side still stands confirmed and the
             other side never confirmed: they tapped Stop for now (the stop
             route nulls the code whoever calls it). E5. With THEIR confirm
             in, a nulled code is the confirm route mid-completion — it clears
             the code before the deal reads completed — so the poll goes on. */
          set({ phase: "stopped", code: null });
          stopClocks();
          return;
        }
        /* H3 (status returns the live code since 3f5f6040): another tab of this
           account stopped and started again between two polls, so the server's
           code is a new one and the one on this screen is dead. The new code
           and its countdown replace it. Only over a code that is still up: a
           spent screen stays spent and holds no code. Only through the same
           gate as a restore (mayRestore): a payer's new code may sit over a
           card the other tab could not pay. A code that went away is the stop
           rule above. */
        const fresh = cur.codeExpired || !mayRestore(cur, st) ? null : liveCodeOf(st);
        if (fresh && (fresh !== cur.code || Date.parse(st.expiresAt ?? "") !== Date.parse(cur.expiresAt ?? ""))) {
          set({ code: fresh, expiresAt: st.expiresAt });
        }
        pollBackoff = 1;
      } else if (res.status === 401) {
        /* Once, not every two seconds: the session is gone, and the screen says so. */
        set({ phase: "error", error: SIGNIN_MESSAGE });
        stopClocks();
        return;
      } else if (res.status === 429) {
        pollBackoff = Math.min(pollBackoff * 2, POLL_BACKOFF_MAX_MS / POLL_MS);
      } else if (res.status === 409 || res.status === 404 || res.status === 403) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        if (!live(g)) return;
        set({ phase: "error", error: body.error ?? FAILURE_MESSAGE });
        stopClocks();
        return;
      }
    } catch {
      /* Offline while waiting: keep asking. The screen already says it waits. */
    }
    if (!live(g)) return;
    const again = state();
    if (again && again.phase === "code" && pollTimer === null) {
      pollTimer = setTimer(() => { void poll(); }, Math.min((again.codeExpired ? SPENT_POLL_MS : POLL_MS) * pollBackoff, POLL_BACKOFF_MAX_MS));
    }
  }
  /** Doubled on a 429, back to 1 on the next good answer. */
  let pollBackoff = 1;

  async function requestWakeLock(): Promise<void> {
    const nav = win.navigator as Navigator & { wakeLock?: { request: (t: "screen") => Promise<WakeSentinel> } };
    if (!nav.wakeLock || wakeLock || wakeRequesting) return;
    wakeRequesting = true;
    let l: WakeSentinel | null = null;
    try { l = await nav.wakeLock.request("screen"); } catch { l = null; }
    wakeRequesting = false;
    if (!l) return;
    /* The browser releases the sentinel on its own when the tab is hidden; the
       release event is what lets the next visibilitychange ask again. */
    l.addEventListener?.("release", () => { if (wakeLock === l) wakeLock = null; });
    const s = state();
    if (!s || s.phase !== "code" || s.codeExpired || wakeLock) { void l.release().catch(() => undefined); return; }
    wakeLock = l;
  }
  function releaseWakeLock(): void {
    const l = wakeLock;
    wakeLock = null;
    if (l) void l.release().catch(() => undefined);
  }
  const onVisibility = (): void => {
    const s = state();
    if (win.document.visibilityState === "visible" && s && s.phase === "code" && !s.codeExpired) void requestWakeLock();
  };

  function startClocks(): void {
    stopClocks();
    const s = state();
    if (s && !s.codeExpired) void requestWakeLock();
    /* Every way onto a live code screen comes after this side's payment went
       through, after a start that owed nothing, or after status vouched for the
       code (mayRestore), so a live code here is one this page can stand behind. */
    if (s && s.phase === "code" && s.code && !s.codeExpired && s.offerId && s.expiresAt) shown.set(s.offerId, s.expiresAt);
    tick();
    pollTimer = setTimer(() => { void poll(); }, s && s.codeExpired ? SPENT_POLL_MS : POLL_MS);
  }

  /* ── building the confirm screen from the offer ───────────────────────── */

  async function readOffer(offerId: string): Promise<ApiOffer | null> {
    const res = await authFetch(`/api/offers/${encodeURIComponent(offerId)}`);
    if (!res.ok) return null;
    return (await res.json()) as ApiOffer;
  }

  function stateFromOffer(o: ApiOffer, threadId: string | null, extra: Partial<InPersonState>): InPersonState {
    const role: Role = o.offer.fromUserId === myUserId ? "buyer" : "seller";
    const kind = kindOf(o.offer.offerType);
    const other = role === "buyer" ? o.toUser : o.fromUser;
    const listingItem: InPersonItem[] = o.listing ? [{ title: o.listing.title, image: o.listing.images?.[0] ?? null }] : [];
    const offered: InPersonItem[] = (o.offeredItems || []).map((it) => ({ title: it.name, image: it.image ?? null }));
    /* The seller's side is the anchor AND every piece the buyer asked for beyond it
       (card 1219708926884343, H1). The deals list carries the offered side only
       (lib/offeredItems.ts), so its row never had them; this offer read carries both
       sides. Without them each person confirmed seeing the anchor alone, while the
       server's completion traded every piece. */
    /* The anchor is already first; a requested row naming the same listing would paint it twice.
       Every sender filters it out (exchangeSend, dealCounter, makeOffer), but the server does not. */
    const requested: InPersonItem[] = (o.requestedItems || [])
      .filter((it) => !(it.listingId && it.listingId === o.listing?.id))
      .map((it) => ({ title: it.name, image: it.image ?? null }));
    const sellerSide: InPersonItem[] = [...listingItem, ...requested];
    const fee = feePayers(o.offer.offerType)[role];
    return {
      threadId,
      offerId: o.offer.id,
      role,
      kind,
      with: other.username,
      withAvatar: other.avatar ?? null,
      youGet: role === "buyer" ? sellerSide : offered,
      youGive: role === "buyer" ? offered : sellerSide,
      cash: o.offer.cashAmount ?? 0,
      sellerCash: o.offer.requestedCashAmount ?? 0,
      fee,
      feeCents: fee ? 100 : 0,
      phase: "confirm",
      second: false,
      fromCode: false,
      code: null,
      codeExpired: false,
      expiresAt: null,
      clientSecret: null,
      /* Only the element's own onReady enables a payer's button: a hook that
         is installed but still loading Stripe.js (or failed to) must not let a
         tap record a confirm whose $1 cannot be paid. */
      payReady: false,
      payWallet: false,
      payMode: "card",
      payErr: null,
      confirmErr: null,
      busy: false,
      error: null,
      hidden: false,
      notRight: false,
      typing: false,
      receipt: null,
      payment: null,
      refusal: null,
      amountChanged: false,
      ...extra,
    };
  }

  /** The live code on a status answer, or null: none sent (the other side, a
   *  stopped or completed handoff, a code past its time on the server's clock,
   *  a server from before 3f5f6040), or one this phone's clock already counts
   *  as spent, exactly as the countdown would. */
  function liveCodeOf(st: StatusResponse): string | null {
    return st.expiresAt && msLeft(st.expiresAt) > 0 ? st.code ?? null : null;
  }

  /** Whether this side's code may come back from status. A side that owes
   *  nothing (a seller on a purchase, a side an event code waived) always may. A
   *  payer only when status says its own hold is collectable: a payer whose card
   *  was declined, whose 3-D Secure step was left, whose start answer was lost
   *  or whose other tab's card failed has a code over a hold that was never
   *  paid, and the other phone's confirm is refused ("They haven't finished
   *  paying yet") with nothing on this screen to pay with. The field is
   *  `yoursHeld`, the one lane A asked Nick for (card 1219708926884343): true
   *  when this side's own hold is authorized. Status does not send it yet, so
   *  until it does a payer keeps the spent screen; it is read only when present
   *  and boolean true. Or when this page itself showed that code (stillShown):
   *  In person on another deal and back, with no reload in between. */
  function mayRestore(s: Pick<InPersonState, "fee" | "offerId">, st: StatusResponse): boolean {
    return !s.fee || st.yoursHeld === true || stillShown(s.offerId, st);
  }

  /** This page showed this offer's live code itself, and status still names its
   *  expiry: the identity a restart elsewhere cannot keep, since every start
   *  mints a new expiry (a code that is no longer there is liveCodeOf's to
   *  refuse). Anything else is a code this page cannot stand behind (a Stop, a
   *  restart in another tab, a code run out), and the memory of the old one is
   *  dropped. */
  function stillShown(offerId: string | null, st: StatusResponse): boolean {
    const kept = offerId ? shown.get(offerId) : undefined;
    if (!kept) return false;
    if (st.expiresAt && Date.parse(st.expiresAt) === Date.parse(kept)) return true;
    shown.delete(offerId!);
    return false;
  }

  /** The screen for a side that already stands confirmed but whose code is not
   *  on this phone (a reload, In person on another deal and back, a start whose
   *  answer was lost). H3: status returns the live code to the phone that
   *  started it since 3f5f6040, so that code comes back with its countdown from
   *  status's expiresAt, and startClocks gives it the 2 s poll and the screen
   *  held awake, as a fresh start does. Status quotes no payment to a side that
   *  has confirmed and no client secret comes with it, so nothing on this screen
   *  names an amount or can charge. Without a live code, or when `restore` is
   *  false (mayRestore), it is the spent screen, as before: Stop for now, then
   *  start again, while the poll still watches for a completion. */
  function waitingScreen(st: StatusResponse, restore: boolean): Partial<InPersonState> {
    const code = restore ? liveCodeOf(st) : null;
    return { phase: "code", code, expiresAt: st.expiresAt, codeExpired: !code, busy: false, payErr: null, confirmErr: null, hidden: false };
  }

  /* ── the deal page's In person ────────────────────────────────────────── */

  /** A screen with no offer to build from (the offer read failed): app.js titles its sheet "In person". */
  function blankScreen(threadId: string | null, offerId: string | null, extra: Partial<InPersonState>): InPersonState {
    return { threadId, offerId, role: "buyer", kind: "purchase", with: "", withAvatar: null, youGet: [], youGive: [], cash: 0, fee: false, feeCents: 0, phase: "error", second: false, fromCode: false, code: null, codeExpired: false, expiresAt: null, clientSecret: null, payReady: false, payWallet: false, payMode: "card", payErr: null, confirmErr: null, busy: false, error: null, hidden: false, notRight: false, typing: false, receipt: null, payment: null, refusal: null, amountChanged: false, ...extra };
  }

  /** IP4a M4: In person refused, or no answer at all. The sheet opens (never a toast) with the server's own sentence,
   *  ours only when it sent none; a note only for a deal-level refusal; and Try again only where waiting can help
   *  (retryHelps). A refusal with its own screen (refusalOf) keeps it, with Back only. */
  function refusedOnOpen(threadId: string, offerId: string, o: ApiOffer | null, httpStatus: number, body: { error?: unknown; code?: unknown }): void {
    const sentence = typeof body.error === "string" && body.error ? body.error : FAILURE_MESSAGE;
    const mapped = refusalOf(body);
    const refusal: Refusal | null = mapped ?? (body.code === "tax_location_unavailable" ? "taxUnavailable" : null);
    const extra: Partial<InPersonState> = { phase: "error", error: sentence, refusal, retry: !mapped && retryHelps(httpStatus, sentence) ? "open" : null };
    set(null);
    set(o ? stateFromOffer(o, threadId, extra) : blankScreen(threadId, offerId, extra));
  }

  /** `retrying`: the error sheet's Try again. The sheet stays up with its button disabled until the answers land. */
  async function open(threadId: string, retrying = false): Promise<void> {
    const cur = state();
    if (!retrying && cur && cur.threadId === threadId && cur.phase !== "done") {
      set({ hidden: false });
      return;
    }
    /* The list row's offer first: it is the row that put In person on screen, and every list
       read rewrites it. The detail pass's id goes stale when a counter is accepted elsewhere,
       and the sheet would then list a superseded offer's pieces (IP2 review A3). */
    const offerId = w.__gbzDisputeContext?.(threadId)?.offerId ?? w.__gbzThreadOfferId?.(threadId) ?? null;
    if (!offerId) { notify(UNRESOLVED_OFFER_MESSAGE); return; }
    if (opening) return;
    opening = true;
    reset();
    const g = gen;
    if (retrying) set({ busy: true });
    let o: ApiOffer | null = null;
    try {
      const [oRes, stRes] = await Promise.all([
        authFetch(`/api/offers/${encodeURIComponent(offerId)}`),
        authFetch(`/api/offers/${encodeURIComponent(offerId)}/in-person/status`),
      ]);
      if (!live(g)) return;
      if (oRes.status === 401 || stRes.status === 401) { if (retrying) set(null); notify(SIGNIN_MESSAGE); return; }
      o = oRes.ok ? ((await oRes.json()) as ApiOffer) : null;
      /* A refused offer read keeps its own status and sentence for the sheet (adversarial pass 1 m-3). */
      const oBody = oRes.ok ? null : ((await oRes.json().catch(() => ({}))) as { error?: unknown; code?: unknown });
      if (!live(g)) return;
      if (!stRes.ok) {
        /* overCap / sellerAddsCash / a missing tax address said before any card is typed get their own screen;
           every other refusal the sheet with the server's sentence (M4). */
        const body = (await stRes.json().catch(() => ({}))) as { error?: unknown; code?: unknown };
        if (!live(g)) return;
        refusedOnOpen(threadId, offerId, o, stRes.status, body);
        return;
      }
      if (!o) { refusedOnOpen(threadId, offerId, null, oRes.status, oBody ?? {}); return; }
      const st = (await stRes.json()) as StatusResponse;
      if (!live(g)) return;
      if (st.completed) { if (retrying) set(null); notify(ALREADY_COMPLETE_MESSAGE); return; }
      const s = stateFromOffer(o, threadId, { second: st.theirs && !st.yours });
      const quote = readPayment(st);
      if (quote.kind === "ok") Object.assign(s, withPayment(quote.payment));
      /* Unreadable: no confirm screen and no pay button (a side already
         confirmed goes on to its waiting screen below, which shows no amount). */
      else if (quote.kind === "bad" && !st.yours) Object.assign(s, { phase: "error", error: AMOUNT_UNAVAILABLE });
      const waiting = st.yours ? waitingScreen(st, mayRestore(s, st)) : null;
      /* Started, for the summary's note: a side that stands confirmed. The
         server never clears inPersonStartedAt after a stop, so
         startedInPerson alone would mark a deal that went back to shipping.
         An unreadable quote marks it with no amount, never the $1 fee row.
         So does every waiting screen without a quote, restored or spent:
         status quotes only a side that has not confirmed, and the $1 row would
         be false over a card payment (unknownMark). */
      mark(threadId, (st.yours || st.theirs) ? (quote.kind === "bad" ? unreadableMark() : waiting && quote.kind !== "ok" ? unknownMark() : markOf(s)) : null);
      set(null);
      set(s);
      if (waiting) {
        set(waiting);
        startClocks();
      }
    } catch {
      if (live(g)) refusedOnOpen(threadId, offerId, o, 0, {});
    } finally {
      opening = false;
    }
  }

  /* ── #/h/<CODE> ────────────────────────────────────────────────────────── */

  async function resolveRoute(): Promise<void> {
    const m = win.location.hash.match(HANDOFF_ROUTE);
    if (!m) return;
    const code = m[1].toUpperCase();
    const cur = state();
    /* The same code already on screen (or resolving): the E1 return sets the
       hash and the hashchange follows, and both must not resolve twice. */
    if (cur && cur.fromCode && cur.code === code) return;
    reset();
    const g = gen;
    set(null);
    set({ phase: "resolving", fromCode: true, code, codeExpired: false, threadId: null, offerId: null, role: "buyer", kind: "purchase", with: "", withAvatar: null, youGet: [], youGive: [], cash: 0, fee: false, feeCents: 0, second: true, expiresAt: null, clientSecret: null, payReady: false, payWallet: false, payMode: "card", payErr: null, confirmErr: null, busy: false, error: null, hidden: false, notRight: false, typing: false, receipt: null, payment: null, refusal: null, amountChanged: false });
    try {
      const res = await authFetch(`/api/offers/in-person/resolve/${encodeURIComponent(code)}`);
      if (!live(g)) return;
      if (res.status === 401) { set({ phase: "signin" }); return; }
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        if (!live(g)) return;
        /* overCap / sellerAddsCash before E2: a 409 here is not a code to replace. */
        const refusal = refusalOf(body);
        if (refusal) { set({ phase: "error", error: body.error ?? FAILURE_MESSAGE, refusal }); return; }
        /* IP4b (H4c): a 5xx with no sentence is the network's, not a refusal: Try again reads the code again. */
        if (res.status >= 500 && !body.error) { set({ phase: "error", error: FAILURE_MESSAGE, reread: true }); return; }
        /* E2 is for a code the server does not honor (unknown, expired, your
           own); anything else is a plain refusal with its sentence and a way back. */
        const e2 = res.status === 404 || res.status === 409 || res.status === 410;
        /* IP4a M5: a code this tab finished resolves to nothing (confirm nulls it), and asking for a new one is
           wrong: the deal is complete, in the server's own sentence, and the screen says where the receipt is. */
        const done = e2 ? finishedHere(code) : null;
        if (done) { set({ phase: "complete", error: ALREADY_COMPLETE_MESSAGE, offerId: done.offerId }); return; }
        set({ phase: e2 ? "expired" : "error", error: body.error ?? FAILURE_MESSAGE });
        return;
      }
      const r = (await res.json()) as ResolveResponse;
      const o = await readOffer(r.offerId);
      if (!live(g)) return;
      if (!o) { set({ phase: "error", error: FAILURE_MESSAGE, reread: true }); return; }
      const s = stateFromOffer(o, null, { second: r.otherSideConfirmed, fromCode: true, code, expiresAt: r.expiresAt });
      const quote = readPayment(r);
      if (quote.kind === "ok") Object.assign(s, withPayment(quote.payment));
      else if (quote.kind === "bad") Object.assign(s, { phase: "error", error: AMOUNT_UNAVAILABLE });
      /* No quote, and not the $1 (or nothing) the screen would print. */
      else if (!isScreenFee(r.feeCents)) Object.assign(s, { phase: "error", error: AMOUNT_UNAVAILABLE });
      else {
        s.feeCents = r.feeCents;
        s.fee = r.feeCents > 0;
      }
      set(s);
    } catch {
      /* No answer, or none in REQUEST_TIMEOUT_MS (IP4b): the shared line, and Try again reads the code again. */
      if (live(g)) set({ phase: "error", error: FAILURE_MESSAGE, reread: true });
    }
  }

  const onHashChange = (): void => { void resolveRoute(); };

  /* ── the writes ────────────────────────────────────────────────────────── */

  /* Round 4 (MINOR-C): E4's kept start, by its key, while it is on the wire (see retryLater). */
  const keptStarts = new Map<string, KeptStart>();
  async function post(path: string, body: unknown, key: string): Promise<Response> {
    const res = await authFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": key },
      body: JSON.stringify(body ?? {}),
    });
    /* Round 4 (MINOR-C): a kept start hears its answer here, even after its screen has moved on (start() is IP3's). One
       the server refused started nothing: the route decides every refusal, its 502 included, before it writes the row. */
    const kept = keptStarts.get(key);
    if (kept && !res.ok) kept.refused = true;
    return res;
  }

  async function readStatus(offerId: string): Promise<StatusResponse | null> {
    try {
      const res = await authFetch(`/api/offers/${encodeURIComponent(offerId)}/in-person/status`);
      return res.ok ? ((await res.json()) as StatusResponse) : null;
    } catch { return null; }
  }

  /** start (the first person). Resolves the answer, or null after painting
   *  the refusal. A 409 is read against status first: this side already
   *  stands confirmed (a retry whose first attempt landed — the server does
   *  not honor the idempotency key) gets the waiting screen; the other side
   *  went first (theirsAlready) becomes the second person's screen. */
  async function start(s: InPersonState, key: string, g: number): Promise<StartResponse | null> {
    if (!s.offerId) return null;
    const res = await post(`/api/offers/${encodeURIComponent(s.offerId)}/in-person/start`, {}, key);
    if (!live(g)) return null;
    if (res.status === 401) { notify(SIGNIN_MESSAGE); set({ busy: false, phase: "confirm" }); return null; }
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!live(g)) return null;
      /* overCap / sellerAddsCash: their own screen, before the 409 is read as a race. */
      const refusal = refusalOf(body);
      if (refusal) { set({ phase: "error", error: body.error ?? FAILURE_MESSAGE, refusal, busy: false }); return null; }
      if (res.status === 409) {
        const st = await readStatus(s.offerId);
        if (!live(g)) return null;
        if (st && st.completed) { await finish(s, null, g, readPayment(st)); return null; }
        if (st && st.yours) {
          mark(s.threadId, markOf(s));
          /* H3: a start whose answer was lost gets status's live code back
             through the same gate as In person's (mayRestore). A payer's intent
             came on the lost answer, so this phone never paid it: their way on
             stays the spent screen's Stop for now, then start again. */
          set(waitingScreen(st, mayRestore(s, st)));
          startClocks();
          return null;
        }
        if (st && st.theirs) {
          /* They went first: this phone is the second one now, and the way in
             is their code (the deal-page sheet takes it typed). */
          mark(s.threadId, markOf(s));
          set({ second: true, phase: "confirm", busy: false, confirmErr: body.error ?? null });
          return null;
        }
      }
      /* IP4a M4: a sentence that says try again (or a 5xx) gets Try again, which puts the confirm screen back:
         start refuses before it records anyone, so the next tap is a fresh start. */
      const sentence = body.error ?? FAILURE_MESSAGE;
      set({ phase: "error", error: sentence, busy: false, retry: retryHelps(res.status, sentence) ? "confirm" : null });
      return null;
    }
    return (await res.json()) as StartResponse;
  }

  /** `captured` is the server's payment object off the answer that said
   *  completed (confirm's, or status's on the first phone). Only a checked one
   *  reaches the receipt; anything else leaves the receipt claiming no charge. */
  async function finish(s: InPersonState, points: number | null, g: number, captured: PaymentRead = { kind: "absent" }): Promise<void> {
    stopClocks();
    if (s.offerId) shown.delete(s.offerId);
    /* The server said completed on an in-person screen: this tab now knows (IP4a M5, M7), whatever the screen does next. */
    rememberDone(s.offerId, s.fromCode ? s.code : null);
    const paid = captured.kind === "ok" ? { payment: captured.payment } : {};
    let receipt: InPersonReceipt = { ref: "", when: null, points, ...paid };
    if (s.offerId) {
      try {
        const res = await authFetch(`/api/receipts/${encodeURIComponent(s.offerId)}`);
        if (res.ok) {
          const r = (await res.json()) as ReceiptResponse;
          receipt = { ref: r.refNum, when: r.acceptedAt ?? null, points, ...paid };
        }
      } catch { /* the receipt read is best effort; the deal is complete either way */ }
    }
    if (!live(g)) return;
    mark(s.threadId, null);
    unmountPayHost();
    if (s.fromCode) replaceCodeRoute(s);
    /* hidden: false — the receipt shows itself even when the sheet had been
       closed with ✕ (the handoff: "moves to the receipt on its own"). */
    /* An event code waived this side after its screen took the $1 quote: the
       server's $0 "none" replaces it, so the receipt claims no charge. */
    const waived = captured.kind === "ok" && captured.payment.model === "none"
      ? { fee: false, payment: captured.payment } : {};
    /* A screen with no quote of its own (a code restored from status, which
       quotes only a side that has not confirmed): the receipt takes what the
       server says it captured, so a card payment never prints as the $1 or as
       cash. No payment on the answer stays today's $1 receipt (rule 1). */
    const adopted = captured.kind === "ok" && !s.payment ? withPayment(captured.payment) : {};
    set({ phase: "done", receipt, busy: false, notRight: false, hidden: false, confirmErr: null, ...adopted, ...waived });
    /* The list, whoever we are: the scanner's Deals also stops offering In
       person on a deal that is done. The thread only when this screen has one. */
    await refreshDeals(win, authFetch, myUserId).catch(() => null);
    if (s.threadId) await installDealThread(win, authFetch, s.threadId, myUserId).catch(() => false);
  }

  /** The second person's $1: one hold per person, returned again while it is
   *  usable. Resolves the secret to confirm (null when nothing is owed or the
   *  hold is already authorized). Throws with the server's sentence. */
  async function authorizeFee(s: InPersonState, g: number): Promise<{ clientSecret: string | null; already: boolean; changed?: boolean }> {
    if (!s.offerId) throw new Error(FAILURE_MESSAGE);
    if (s.clientSecret) return { clientSecret: s.clientSecret, already: paidHandled.has(s.clientSecret) };
    const res = await post(`/api/offers/${encodeURIComponent(s.offerId)}/${AUTHORIZE_PATH}`, {}, idempotencyKey());
    if (!live(g)) throw new Error(FAILURE_MESSAGE);
    const body = (await res.json().catch(() => ({}))) as Partial<AuthorizeResponse> & { error?: string };
    if (!live(g)) throw new Error(FAILURE_MESSAGE);
    if (res.status === 401) { notify(SIGNIN_MESSAGE); throw new Error(SIGNIN_MESSAGE); }
    if (!res.ok) {
      const refusal = refusalOf(body);
      if (refusal) throw new RefusedError(body.error ?? FAILURE_MESSAGE, refusal);
      /* The handoff is over: mapped exactly as confirmSecond maps confirm's. */
      const sentence = body.error ?? FAILURE_MESSAGE;
      const code = (body as { code?: unknown }).code;
      if (res.status === 410) throw new HandoffEndedError(sentence, "expired");
      if (res.status === 409 && (code === "notStarted" || /started/i.test(sentence))) throw new HandoffEndedError(sentence, "stopped");
      throw new Error(sentence);
    }
    const secret = body.clientSecret ?? null;
    const quote = readPayment(body);
    if (quote.kind === "absent" && !s.payment) {
      /* No quote, and not the $1 (or nothing) the screen prints: nothing is paid. */
      if (!isScreenFee(body.feeCents ?? s.feeCents)) throw new AmountError();
      set({ clientSecret: secret, feeCents: typeof body.feeCents === "number" ? body.feeCents : s.feeCents, fee: (body.feeCents ?? s.feeCents) > 0 });
      if (secret && body.alreadyAuthorized) paidHandled.add(secret);
      return { clientSecret: secret, already: !!body.alreadyAuthorized || !secret };
    }
    /* The server's non-payer answer (offersInPerson.ts's early return for a
       seller on a purchase) carries no quote yet. On a screen whose quote says
       this person pays nothing, a zero with no intent agrees with it, and
       nothing is charged either way, so they go on to confirm. */
    if (quote.kind === "absent" && s.payment?.model === "none" && body.feeCents === 0 && !secret) {
      return { clientSecret: null, already: true };
    }
    /* Item mode: the intent's own quote, or nothing is paid. Absent here after
       the screen showed a quote is as unreadable as a bad one. */
    if (quote.kind !== "ok") throw new AmountError();
    const p = quote.payment;
    /* A payer's quote needs an intent and a non-payer's must not have one. */
    if ((p.model !== "none") !== !!secret) throw new AmountError();
    const changed = quoteChanged(s, p);
    if (secret && body.alreadyAuthorized) paidHandled.add(secret);
    if (changed) {
      /* phase "confirm" (IP4b): when this answer came on E4's retry, the new amount goes on the confirm screen, not under E4. */
      set({ clientSecret: secret, ...withPayment(p), amountChanged: true, payReady: false, busy: false, phase: "confirm" });
      unmountPayHost();
      return { clientSecret: secret, already: false, changed: true };
    }
    set({ clientSecret: secret, ...withPayment(p) });
    return { clientSecret: secret, already: !!body.alreadyAuthorized || !secret };
  }

  /** The second confirm. The server verifies both holds and completes. A
   *  refusal that is not about a fee is read against status first: a confirm
   *  whose answer was lost (E4's retry, a 5xx after the row moved) may have
   *  completed the deal, and then the receipt is what is true. */
  async function confirmSecond(s: InPersonState, key: string, g: number): Promise<void> {
    if (!s.offerId) return;
    const res = await post(`/api/offers/${encodeURIComponent(s.offerId)}/${CONFIRM_PATH}`, { code: s.code }, key);
    if (!live(g)) return;
    if (res.status === 401) { notify(SIGNIN_MESSAGE); set({ busy: false, phase: "confirm" }); return; }
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
      if (!live(g)) return;
      const sentence = body.error ?? FAILURE_MESSAGE;
      const refusal = refusalOf(body);
      if (refusal) { set({ phase: "error", error: sentence, refusal, busy: false }); return; }
      /* Theirs is a 409 like a race: told apart by the server's code
         (feeNotAuthorizedTheirs), or its sentence when no code came. */
      if (res.status === 402 || (res.status === 409 && (body.code === "feeNotAuthorizedTheirs" || /^They haven['’]t finished paying yet/.test(sentence)))) {
        /* A fee not ready, mine or theirs: stay here with the sentence; the
           next tap tries again. */
        set({ phase: "confirm", busy: false, confirmErr: sentence });
        return;
      }
      /* The deal may have completed meanwhile (settledMeanwhile's read, kept here so the next step can use it). */
      const st = await readStatus(s.offerId);
      if (!live(g)) return;
      if (st && st.completed) { await finish(s, null, g, readPayment(st)); return; }
      /* IP4b round 3 (adversarial MINOR-1): "already confirmed", with status agreeing that this side's confirm stands
         and the deal not completed yet, is this side's own confirm landing first (a retry after a confirm that ran past
         20 s, or M9's re-tap after a 500 that came after the row moved) while the server finishes the deal: E4,
         reading status until it completes. A status that disagrees keeps today's screen with the server's sentence. */
      if (res.status === 409 && st && st.yours && (body.code === "alreadyConfirmed" || /^You['\u2019]ve already confirmed your side/.test(sentence))) { awaitCompletion(key, g); return; }
      /* IP4b (M9): a 5xx is the server failing, not refusing. Any hold stands and a re-tap reuses it (authorizeFee
         returns the held secret; nothing is paid twice), so the confirm screen stays with its button live. */
      if (res.status >= 500) { set({ phase: "confirm", busy: false, confirmErr: s.fee ? `${CONFIRM_RETRY_MESSAGE} ${CHARGED_ONCE}` : CONFIRM_RETRY_MESSAGE }); return; }
      if (res.status === 410) { set({ phase: "expired", error: sentence, busy: false }); return; }
      set({ phase: res.status === 409 && /started/i.test(sentence) ? "stopped" : "error", error: sentence, busy: false });
      return;
    }
    const r = (await res.json()) as ConfirmResponse;
    if (r.completed) { await finish(s, typeof r.pointsAwarded === "number" ? r.pointsAwarded : null, g, readPayment(r)); return; }
    set({ phase: "error", error: FAILURE_MESSAGE, busy: false });
  }

  /** The deal completed under a refusal (a confirm that landed but whose answer was lost): the receipt, then. */
  async function settledMeanwhile(s: InPersonState, g: number): Promise<boolean> {
    if (!s.offerId) return false;
    const st = await readStatus(s.offerId);
    if (!live(g)) return true;
    if (st && st.completed) { await finish(s, null, g, readPayment(st)); return true; }
    return false;
  }

  /* IP4b: E4's kept write runs again every RETRY_MS, except while a Stop tapped on E4 is out. Then it waits in
     `parked` and runs only if that stop does not go through; a card confirm or a confirm reached while the stop is
     out waits there too, so no new hold and no completion start after the person asked to stop. */
  let parked: (() => void) | null = null;
  /* Round 3 (MAJOR-1): set while a Stop on E4 got no answer and status is being read to learn whether it landed.
     Writes wait exactly as they do while the stop itself is out. */
  let settling = false;
  /* Round 4: the settle's own clock (its status re-read, or the stop sent again). It is not one of the code screen's
     clocks, so a kept start that answers meanwhile and starts them cannot cancel it. */
  let settleTimer: number | null = null;
  function settleLater(run: () => void): void {
    if (settleTimer !== null) clearTimer(settleTimer);
    settleTimer = setTimer(() => { settleTimer = null; run(); }, RETRY_MS);
  }
  /* Round 5 (rule 15, 2026-10-21): once the person has tapped Stop on E4, the kept write never resumes, whatever the
     stop's answer. Set by that tap; cleared with the screen (reset), or when the person taps for a new write
     themselves (Confirm, Try again). */
  let stoppedOnE4 = false;
  /** A write must wait: a stop is out, or the person tapped Stop on E4 on this screen (which covers the settle). */
  const holdWrites = (): boolean => stopping || stoppedOnE4;
  function park(run: () => void): void {
    parked = run;
    /* Round 4 (MINOR-B), round 5: a write held by a Stop tapped on E4 waits on E4, whose Stop stays live. */
    if (stoppedOnE4) set({ phase: "offline", busy: true });
    if (!holdWrites()) armParked();
  }
  function armParked(): void {
    if (retryTimer !== null) clearTimer(retryTimer);
    retryTimer = setTimer(() => { retryTimer = null; const run = parked; parked = null; run?.(); }, RETRY_MS);
  }
  /* Round 3 (MINOR-3): the kept write E4 has on the wire, so a Stop that ends the screen can learn later whether a
     start landed after it. Round 4 (MINOR-C): with what that start heard, because only one that answered ok, or got
     no answer at all, can have started anything. */
  type KeptStart = { done: Promise<void>; refused: boolean };
  let e4Attempt: KeptStart | null = null;
  /** E4: the write could not reach the server (or got no answer in REQUEST_TIMEOUT_MS): kept, and tried again. */
  function retryLater(key: string, g: number): void {
    set({ phase: "offline", busy: true });
    park(() => {
      const attempt: KeptStart = { done: Promise.resolve(), refused: false };
      keptStarts.set(key, attempt);
      attempt.done = attemptConfirm(key, g);
      e4Attempt = attempt;
      void attempt.done.finally(() => {
        if (e4Attempt === attempt) e4Attempt = null;
        if (keptStarts.get(key) === attempt) keptStarts.delete(key);
      });
    });
  }
  /** Round 3 (MINOR-1): this side's confirm stands on the server and the deal is still completing. E4, reading
   *  status every RETRY_MS (no further confirm is posted) until it says completed. Should this side's confirm be gone
   *  instead (a stop came first), the kept write runs and the server's answer decides the screen. */
  function awaitCompletion(key: string, g: number): void {
    set({ phase: "offline", busy: true });
    park(() => { void readCompletion(key, g); });
  }
  async function readCompletion(key: string, g: number): Promise<void> {
    const s = state();
    if (!live(g) || !s || !s.offerId) return;
    const st = await readStatus(s.offerId);
    if (!live(g)) return;
    if (st && st.completed) { await finish(s, null, g, readPayment(st)); return; }
    if (!st || st.yours) { awaitCompletion(key, g); return; }
    await attemptConfirm(key, g);
  }
  /** H4a (IP4b): a payment that fails on E4's retry (a decline after a lost authorize-fee) goes back to the confirm
   *  screen and its decline controls; it used to stay on E4 with none. */
  function offConfirmIfOffline(): Partial<InPersonState> {
    return state()?.phase === "offline" ? { phase: "confirm" } : {};
  }

  /** The second confirm with E4 behind it: a confirm that cannot reach the
   *  server is kept and retried under the same key, like a first person's start. */
  async function confirmOrRetry(key: string, g: number): Promise<void> {
    const s = state();
    if (!live(g) || !s) return;
    if (holdWrites()) { park(() => { void confirmOrRetry(key, g); }); return; }
    try {
      await confirmSecond(s, key, g);
    } catch (err) {
      if (!live(g)) return;
      if (isNetworkFailure(err)) { retryLater(key, g); return; }
      set({ busy: false, phase: "confirm", confirmErr: FAILURE_MESSAGE });
    }
  }

  /** One attempt of the confirm (or its offline retry under the same key). */
  async function attemptConfirm(key: string, g: number): Promise<void> {
    const s = state();
    if (!live(g) || !s || !s.offerId || (s.phase !== "confirm" && s.phase !== "offline")) return;
    try {
      if (!s.second) {
        /* A payer's card is checked BEFORE start, because start records this
           side's confirm and mints the $1 intent: an empty card field must not
           count them as confirmed. Only on the tap itself (the offline retry
           already passed this), and the sentence is the element's own. */
        const hook = w.__gbzInPersonPay;
        if (s.fee && !s.clientSecret && s.phase === "confirm" && hook?.validate) {
          try {
            await hook.validate();
          } catch (err) {
            if (!live(g)) return;
            const message = err instanceof Error ? err.message : String(err);
            if (message === PAY_FORM_FAILED) { formFailedMidTap(); return; }
            set({ busy: false, payErr: message || FAILURE_MESSAGE });
            return;
          }
          if (!live(g)) return;
        }
        const r = await start(s, key, g);
        if (!r || !live(g)) return;
        if (s.payment || readPayment(r).kind !== "absent") { await startedWithQuote(s, r, g); return; }
        if (!isScreenFee(r.feeCents)) { startedUnreadable(s); return; }
        mark(s.threadId, { started: true, fee: s.fee });
        if (r.clientSecret) {
          set({ clientSecret: r.clientSecret, code: r.code, expiresAt: r.expiresAt, feeCents: r.feeCents, fee: true, phase: "confirm" });
          await payThen(r.clientSecret, g, () => {
            set({ phase: "code", codeExpired: false, busy: false, payErr: null });
            startClocks();
          });
          return;
        }
        /* hidden stays as it is: a ✕ during the start is respected, and In
           person brings the code back; only a completion shows itself. */
        set({ phase: "code", code: r.code, codeExpired: false, expiresAt: r.expiresAt, feeCents: r.feeCents, busy: false });
        startClocks();
        return;
      }
      /* The second person: their $1 first (the server says zero for a seller
         on a purchase), then the confirm. */
      const fee = await authorizeFee(s, g);
      if (!live(g)) return;
      /* A different amount than the screen showed: it is on screen now, and nothing was paid. */
      if (fee.changed) return;
      if (fee.clientSecret && !fee.already) {
        await payThen(fee.clientSecret, g, () => confirmOrRetry(idempotencyKey(), g));
        return;
      }
      await confirmOrRetry(key, g);
    } catch (err) {
      if (!live(g)) return;
      if (err instanceof RefusedError) { set({ phase: "error", error: err.message, refusal: err.refusal, busy: false }); return; }
      if (err instanceof AmountError) { set({ phase: "error", error: AMOUNT_UNAVAILABLE, busy: false }); return; }
      if (isNetworkFailure(err)) { retryLater(key, g); return; }
      /* authorize-fee refused: the deal may have completed meanwhile (the
         answer to an earlier confirm lost), else the sentence, and the button stays. */
      if (s.second && (await settledMeanwhile(s, g))) return;
      /* ...unless the handoff is over: its own screen, as confirm's refusal gets. */
      if (err instanceof HandoffEndedError) { set({ phase: err.phase, error: err.message, busy: false }); return; }
      const message = err instanceof Error ? err.message : String(err);
      set({ busy: false, phase: "confirm", confirmErr: message === SIGNIN_MESSAGE ? null : message || FAILURE_MESSAGE });
    }
  }

  /** start recorded this side's confirm, but the amount on its answer could not
   *  be trusted: the deal is marked started with no amount on its summary, the
   *  screen says so with no pay button, and nothing is paid. */
  function startedUnreadable(s: InPersonState): void {
    mark(s.threadId, unreadableMark());
    set({ phase: "error", error: AMOUNT_UNAVAILABLE, busy: false });
  }

  /** The first person's start answered in item mode (a quote on the screen, or
   *  one on the answer): pay only an intent whose quote is the one on screen. */
  async function startedWithQuote(s: InPersonState, r: StartResponse, g: number): Promise<void> {
    if (takeStartQuote(s, r) !== "pay" || !r.clientSecret) return;
    await payThen(r.clientSecret, g, () => {
      set({ phase: "code", codeExpired: false, busy: false, payErr: null });
      startClocks();
    });
  }

  /** Puts start's quote on the screen. start has already recorded this side's
   *  confirm, so every way out marks the deal started.
   *    "pay"   the intent is the amount the screen showed: go on and pay it;
   *    "code"  nothing to pay: the code is up;
   *    "stop"  the screen changed (a new amount, or one that could not be
   *            read) and nothing is paid on this tap. */
  function takeStartQuote(s: InPersonState, r: StartResponse): "pay" | "code" | "stop" {
    const quote = readPayment(r);
    /* A payer's quote needs an intent and a non-payer's must not have one. */
    if (quote.kind !== "ok" || (quote.payment.model !== "none") !== !!r.clientSecret) {
      startedUnreadable(s);
      return "stop";
    }
    const p = quote.payment;
    mark(s.threadId, markOf({ ...s, ...withPayment(p) }));
    if (!r.clientSecret) {
      set({ phase: "code", code: r.code, codeExpired: false, expiresAt: r.expiresAt, ...withPayment(p), busy: false });
      startClocks();
      return "code";
    }
    if (quoteChanged(s, p)) {
      set({ clientSecret: r.clientSecret, code: r.code, expiresAt: r.expiresAt, ...withPayment(p), phase: "confirm", busy: false, payReady: false, amountChanged: true });
      unmountPayHost();
      return "stop";
    }
    set({ clientSecret: r.clientSecret, code: r.code, expiresAt: r.expiresAt, ...withPayment(p), phase: "confirm" });
    return "pay";
  }

  /** Card mode: the hook confirms the intent; a decline lands on E3 with the
   *  element's own sentence. The intent counts as paid once, whichever
   *  signal arrives first. */
  async function payThen(clientSecret: string, g: number, then: () => void | Promise<void>): Promise<void> {
    const hook = w.__gbzInPersonPay;
    if (!hook) { set({ busy: false, payErr: PAY_NOT_READY, ...offConfirmIfOffline() }); return; }
    if (paidHandled.has(clientSecret)) { await then(); return; }
    /* IP4b: no new hold while a Stop tapped on E4 is out, or while its outcome is still being read (round 3); this
       payment runs only if the stop did not go through. */
    if (holdWrites()) { park(() => { void payThen(clientSecret, g, then); }); return; }
    pendingPaid = { secret: clientSecret, then, g };
    try {
      await hook.confirmCard();
      if (!live(g)) return;
      if (pendingPaid && pendingPaid.secret === clientSecret) { pendingPaid = null; paidHandled.add(clientSecret); await then(); }
    } catch (err) {
      pendingPaid = null;
      if (!live(g)) return;
      const message = err instanceof Error ? err.message : String(err);
      if (message === PAY_FORM_FAILED) { formFailedMidTap(); return; }
      set({ busy: false, payErr: message || FAILURE_MESSAGE, ...offConfirmIfOffline() });
    }
  }
  /** The form went dead between the tap and the hook (it unmounted or never got live): the failed-form state, so the
   *  screen shows the real Try again in place of the pay button, never the failure line over a live one (adversarial
   *  pass 1 m-2). An intent that already exists is kept: after Try again, the next tap pays that same intent.
   *  Always back on the confirm screen: a failure that lands during the offline retry (E4) must not leave E4 up with
   *  no Try again and no retry left (adversarial pass 2 m-3). */
  function formFailedMidTap(): void {
    unmountPayHost();
    set({ phase: "confirm", busy: false, payReady: false, payFailed: true, payErr: null });
  }
  let pendingPaid: { secret: string; then: () => void | Promise<void>; g: number } | null = null;

  /** A payer whose intent exists pays it; nobody starts or confirms twice. */
  function payExisting(s: InPersonState, secret: string): void {
    /* Round 5 (rule 15): a payment the person taps for (Confirm, Try again) is their own new write, not the kept one. */
    stoppedOnE4 = false;
    const g = gen;
    set({ busy: true, payErr: null, confirmErr: null, amountChanged: false });
    void payThen(secret, g, () => {
      if (!s.second) { set({ phase: "code", codeExpired: false, busy: false }); startClocks(); return; }
      return confirmOrRetry(idempotencyKey(), g);
    });
  }

  /** M8 (IP4b): Try again after PAY_UNCONFIRMED. Re-confirming an intent that did go through only gets Stripe's
   *  refusal, and the payer looped on it (the audit's probe P13). So the hold is read back first, under "Checking
   *  your payment": held, it counts as paid and the next screen takes over (the code, or the confirm, which the
   *  server checks against every hold); not held, the card is confirmed again as before; a read that fails, or gets
   *  no answer in REQUEST_TIMEOUT_MS, is still unknown. A hook without check() re-confirms as before. */
  async function recheck(s: InPersonState, secret: string): Promise<void> {
    const hook = w.__gbzInPersonPay;
    if (hook?.check) {
      const g = gen;
      set({ busy: true, checking: true });
      let read: "held" | "unpaid" | null;
      try { read = await withinTimeout(hook.check(secret), setTimer, clearTimer); } catch { read = null; }
      if (!live(g)) return;
      if (!read) { set({ busy: false, checking: false, payErr: PAY_UNCONFIRMED }); return; }
      set({ checking: false });
      if (read === "held") paidHandled.add(secret);
    }
    payExisting(s, secret);
  }

  async function stop(): Promise<void> {
    const s = state();
    if (!s || !s.offerId || stopping) return;
    if (s.phase === "confirm" && !s.second && !s.code && !s.clientSecret) {
      /* Nothing has been started on the server for this side: the way out is
         to close the screen. A POST here would be refused (notStarted). */
      const offerId = s.offerId;
      reset();
      set(null);
      leaveRouteIfOnIt(offerId);
      return;
    }
    /* IP4b: Stop is tappable on E4. While the stop is out, E4's retry waits (park). Round 5 (rule 15): it never runs
       again, whatever the stop's answer; a refusal shows the server's sentence and E4 stays, its Stop live. A first
       person whose start never reached the server hears "Nobody has started" from the stop route: nothing to undo, so
       the screen ends as a stop does. */
    const fromE4 = s.phase === "offline";
    if (fromE4 && retryTimer !== null) { clearTimer(retryTimer); retryTimer = null; }
    if (fromE4) stoppedOnE4 = true;
    /* Round 3: a Stop tapped while an earlier unanswered stop is being settled takes over from it (round 4: its
       status re-read, or the stop it was about to send again, too). */
    settling = false;
    if (settleTimer !== null) { clearTimer(settleTimer); settleTimer = null; }
    /* Round 3 (MINOR-3): a first person's kept start that is on the wire may land after this stop ends the screen. */
    const lateStart = fromE4 && !s.second ? e4Attempt : null;
    await sendStop(s, gen, fromE4, lateStart, false);
  }

  /** One stop request: the tap's, or (round 4, MINOR-A) the one sent again when status still shows the handoff after
   *  an unanswered stop. `told`: the shared failure line has already been shown for this tap. */
  async function sendStop(s: InPersonState, g: number, fromE4: boolean, lateStart: KeptStart | null, told: boolean): Promise<void> {
    if (!s.offerId) return;
    stopping = true;
    const release = (): void => { if (!fromE4) set({ busy: false }); };
    /* Round 3 (MAJOR-1): the stop may have landed without our knowing (no answer, none in 20 s, or a 5xx: the server
       clears the row before it releases the holds, so even a 500 can come after it landed). */
    let unknown = false;
    set({ busy: true });
    try {
      const res = await post(`/api/offers/${encodeURIComponent(s.offerId)}/in-person/stop`, {}, idempotencyKey());
      if (!live(g)) return;
      if (res.status === 401) { notify(SIGNIN_MESSAGE); release(); return; }
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
        if (!live(g)) return;
        if (fromE4 && res.status >= 500) { unknown = true; return; }
        /* Round 5 (rule 15): "already complete" is a confirm completing the deal, or one that has. */
        if (fromE4 && res.status === 409 && (body.code === "alreadyComplete" || /^This deal is already complete/.test(body.error ?? ""))) { void awaitCompletedAfterStop(s, g); return; }
        const nothingStarted = fromE4 && res.status === 409 && /started/i.test(body.error ?? "");
        if (!nothingStarted) {
          notify(body.error ?? FAILURE_MESSAGE);
          release();
          return;
        }
      }
      await endAsStopped(s, lateStart);
    } catch {
      if (!live(g)) return;
      if (fromE4) unknown = true;
      else { release(); notify(FAILURE_MESSAGE); }
    } finally {
      stopping = false;
      /* Round 5 (rule 15): a refused stop no longer re-arms the kept write; it stays parked, held, until the screen ends. */
      if (fromE4 && live(g)) {
        if (unknown) { settling = true; void settleLostStop(s, g, lateStart, told); }
      }
    }
  }

  /** Round 5 (rule 15): a Stop on E4 answered "already complete". A confirm is completing the deal (the stop route
   *  leaves a both-confirmed row as it found it) or has: E4 reads status every RETRY_MS until it says completed, then
   *  shows the receipt. Nothing is sent, and the kept write stays held. */
  async function awaitCompletedAfterStop(s: InPersonState, g: number): Promise<void> {
    if (!s.offerId) return;
    set({ phase: "offline", busy: true });
    const st = await readStatus(s.offerId);
    if (!live(g)) return;
    if (st && st.completed) { await finish(s, null, g, readPayment(st)); return; }
    settleLater(() => { void awaitCompletedAfterStop(s, g); });
  }

  /** The screen ends as a confirmed stop ends it. Round 3 (MINOR-3): a kept start that was on the wire is watched, and
   *  if it lands after this, it is stopped again. Round 4 (MINOR-C): only one that answered ok or got no answer; one
   *  the server refused started nothing, so a handoff this account begins elsewhere (another tab or device) stands. */
  async function endAsStopped(s: InPersonState, lateStart: KeptStart | null): Promise<void> {
    mark(s.threadId, null);
    /* IP3b: a stop drops this page's memory of the code it showed; here, for every way a stop ends the screen. */
    if (s.offerId) shown.delete(s.offerId);
    reset();
    set(null);
    leaveRouteIfOnIt(s.offerId);
    const offerId = s.offerId;
    if (lateStart && offerId) void lateStart.done.then(() => (lateStart.refused ? undefined : stopIfStartedLate(offerId)));
    await refreshDeals(win, authFetch, myUserId).catch(() => null);
    notify(STOPPED_MESSAGE);
  }

  /** Round 3 (MAJOR-1): a Stop tapped on E4 got no answer it could trust, so it may have landed. Status says which,
   *  before anything else is sent:
   *    this side no longer stands (the first phone's own confirm, or on the second phone the other side's): the stop
   *      landed, or there was nothing to stop, and the screen ends as a stop does;
   *    still standing: the shared failure line shows, and (round 4, MINOR-A) the stop is sent again RETRY_MS later;
   *    status unreadable: status is asked again every RETRY_MS, never the write.
   *  Writes wait the whole time (holdWrites), so a new hold is never taken, and an intent the stop canceled is never
   *  confirmed, after a Stop that reached the server. Only a stop the server answers decides what follows: ok ends
   *  the screen; a refusal with a sentence shows it and keeps E4 with its Stop live (round 5, rule 15: the kept write
   *  never resumes). */
  async function settleLostStop(s: InPersonState, g: number, lateStart: KeptStart | null, told: boolean): Promise<void> {
    if (!s.offerId) return;
    /* Round 4 (MINOR-B): while the outcome is unknown, a write that waits on it waits on E4, with its words and its
       live Stop. A kept answer may have moved the screen to the confirm card, whose button and Stop are disabled. */
    if (parked) set({ phase: "offline", busy: true });
    const st = await readStatus(s.offerId);
    if (!live(g) || !settling) return;
    if (!st) {
      if (!told) notify(FAILURE_MESSAGE);
      settleLater(() => { void settleLostStop(s, g, lateStart, true); });
      return;
    }
    if (s.second ? !st.theirs : !st.yours) { await endAsStopped(s, lateStart); return; }
    /* Round 4 (MINOR-A): still standing is a stop that did not land, or a NEW handoff begun after it did (a kept start
       landing late, or the other person starting again). The two look alike, so the stop goes again; the kept write
       never resumes on this read. */
    if (!told) notify(FAILURE_MESSAGE);
    settleLater(() => { settling = false; void sendStop(s, g, true, lateStart, true); });
  }

  /** Round 3 (MINOR-3): a kept start answered after a Stop ended its screen. If this side now stands confirmed on the
   *  server (the start landed after the stop) and no screen is open for this deal, one more stop, quietly. Showing
   *  the code instead would undo what the person asked for; a second stop costs one request. */
  async function stopIfStartedLate(offerId: string): Promise<void> {
    const open = state();
    if (open && open.offerId === offerId) return;
    const st = await readStatus(offerId);
    const now = state();
    if (!st || !st.yours || st.completed || (now && now.offerId === offerId)) return;
    try {
      await post(`/api/offers/${encodeURIComponent(offerId)}/in-person/stop`, {}, idempotencyKey());
    } catch { /* the code still runs out on its own, and the server releases its holds then */ }
  }

  /** The scanned page has nothing to show once its screen is gone: the deal,
   *  selected when the list knows it. */
  function leaveRouteIfOnIt(offerId: string | null): void {
    if (!HANDOFF_ROUTE.test(win.location.hash)) return;
    try { if (offerId) w.__gbzSelectDealByOffer?.(offerId); } catch { /* the bridge is not there yet */ }
    win.location.hash = "#/deals";
  }

  /** IP4a M5: once the scanned page's receipt shows, the address is Deals, not the spent code, so a reload or a
   *  restored tab lands on Deals. replaceState fires no hashchange, so nothing resolves again and the receipt stays. */
  function replaceCodeRoute(s: InPersonState): void {
    const m = win.location.hash.match(HANDOFF_ROUTE);
    if (!m || m[1].toUpperCase() !== s.code) return;
    try { if (s.offerId) w.__gbzSelectDealByOffer?.(s.offerId); } catch { /* the bridge is not there yet */ }
    try { win.history.replaceState(win.history.state, "", "#/deals"); } catch { /* the address stays; the receipt is right either way */ }
  }

  /* ── the payment slot ──────────────────────────────────────────────────── */

  /* app.js repaints the whole screen on every state change, and the slot's
     container with it. The element must NOT be rebuilt each time, and it must
     not even be MOVED: a detached iframe loses its browsing context and a
     card typed into Stripe's element would be wiped by the tap's own repaint.
     So the element lives in one host that hangs off <body>, positioned over
     the slot, re-placed after each repaint; the slot reserves the host's
     height. mount() runs once per screen; unmount when the screen is cleared
     or leaves the confirm phase. */
  let payHost: HTMLElement | null = null;
  let payHostGen = -1;
  let unmountPay: (() => void) | null = null;
  let hostResize: { disconnect: () => void } | null = null;
  /* The frame loop that keeps the host on its slot while it is mounted, and
     the visual-viewport height the sheet was last fitted to (0: not fitted). */
  let hostFrame: number | null = null;
  let fittedHeight = 0;
  /* IP4a H5: the mounted form's PAY_FORM_TIMEOUT_MS clock, cleared by its ready or its unmount. */
  let payFormTimer: number | null = null;
  function clearPayFormTimer(): void {
    if (payFormTimer !== null) { clearTimer(payFormTimer); payFormTimer = null; }
  }
  /** The card form will not load (the hook said so, or it was not ready in time): the failure line and Try again
   *  in place of the pay button. A late ready still wins (onReady clears this). */
  function payFormFailed(): void {
    const cur = state();
    if (!cur || cur.phase !== "confirm" || !cur.fee || cur.payReady || cur.payFailed) return;
    set({ payFailed: true });
  }

  function unmountPayHost(): void {
    clearPayFormTimer();
    unmountPay?.();
    unmountPay = null;
    hostResize?.disconnect();
    hostResize = null;
    if (hostFrame !== null) { win.cancelAnimationFrame?.(hostFrame); hostFrame = null; }
    fittedHeight = 0;
    payHost?.remove();
    payHost = null;
    payHostGen = -1;
  }

  type PlacedStyle = "display" | "position" | "left" | "top" | "width" | "height" | "bottom" | "zIndex" | "clipPath" | "minHeight" | "maxHeight";
  /** placeHost runs every frame while the element is up: write a style only
   *  when it changes, so an idle frame costs a measurement and nothing else. */
  function put(el: HTMLElement, prop: PlacedStyle, value: string): void {
    if (el.style[prop] !== value) el.style[prop] = value;
  }

  /* Less than this between the two viewports is a scrollbar or a toolbar, not a keyboard. */
  const KEYBOARD_MIN_PX = 80;
  /** iPhone, keyboard up (live show, 2026-10-03): iOS shrinks only the VISUAL
   *  viewport. The fixed backdrop's inset 0 and the sheet's 100dvh stay at the
   *  full layout height, so the sheet ran on under the keyboard with its ✕ out
   *  of reach, and the page behind showed through around it. While the keyboard
   *  is up, the backdrop is fitted to the visual viewport (and the sheet inside
   *  it), so the sheet scrolls on its own and its ✕ stays one drag away. Only
   *  at scale 1, so a pinch-zoom is left alone. app.js repaints the backdrop
   *  from scratch, so this re-applies on every placement. Returns whether the
   *  fitted height changed (the keyboard opening, closing or animating). */
  function fitSheetToKeyboard(sheet: HTMLElement): boolean {
    const back = sheet.parentElement;
    const vv = win.visualViewport;
    const up = !!back && !!vv && Math.abs(vv.scale - 1) < 0.01 && win.innerHeight - vv.height > KEYBOARD_MIN_PX;
    const height = up ? Math.round(vv!.height) : 0;
    if (back) {
      put(back, "top", up ? `${Math.round(vv!.offsetTop)}px` : "");
      put(back, "height", up ? `${height}px` : "");
      put(back, "bottom", up ? "auto" : "");
    }
    put(sheet, "maxHeight", up ? `${Math.max(0, height - 24)}px` : "");
    const changed = height !== fittedHeight;
    fittedHeight = height;
    return changed;
  }

  /** Put the host over the slot, clipped to the sheet it sits in, hidden when
   *  the slot is off screen or a sheet is over it (X1). In the sheet the host is
   *  FIXED (the sheet is, and it scrolls on its own); on the scanned page it is
   *  ABSOLUTE, so it scrolls with the page natively instead of chasing it (a
   *  fixed host on a scrolling page lagged the content, and iOS scrolling the
   *  page to reveal a focused card field moved the slot out from under it again
   *  and again, down to the footer).
   *  The position is CORRECTED by what the host's own rectangle says, not
   *  computed: the move is the slot's rectangle minus the host's, both measured
   *  the same way, which holds for either position, any containing block, and
   *  whatever offset iOS's keyboard puts between the two viewports. The slot's
   *  reserved height is written BEFORE measuring, since writing it can move the
   *  centred sheet. Re-run on every scroll anywhere (capture), resize, visual
   *  viewport change, and every frame while mounted (followHost): a layout
   *  shift with no event (an image loading above the slot, the keyboard's own
   *  animation) left the fields over the sheet's title. */
  function placeHost(): void {
    const host = payHost;
    if (!host) return;
    const s = state();
    const slot = win.document.querySelector<HTMLElement>(PAY_SLOT_SELECTOR);
    if (!slot || !s || s.notRight || s.hidden) { put(host, "display", "none"); return; }
    put(host, "display", "");
    const sheet = slot.closest<HTMLElement>(".ip-modal");
    put(host, "position", sheet ? "fixed" : "absolute");
    put(host, "zIndex", sheet ? "91" : "50");
    const refit = sheet ? fitSheetToKeyboard(sheet) : false;
    const h = host.offsetHeight || 0;
    if (h) put(slot, "minHeight", `${h}px`);
    let r = slot.getBoundingClientRect();
    /* The keyboard just resized the sheet under a card field being typed in
       (the iframe holds focus): scroll the sheet so the element is in view,
       once per resize, never against the person's own scrolling. */
    if (sheet && refit && fittedHeight && host.contains(win.document.activeElement)) {
      const c = sheet.getBoundingClientRect();
      const by = r.top < c.top || h > c.height ? r.top - c.top - 8 : Math.max(0, r.top + h - c.bottom + 8);
      if (by) { sheet.scrollTop += by; r = slot.getBoundingClientRect(); }
    }
    const at = host.getBoundingClientRect();
    /* A host with no box yet (nothing laid out in it) has no rectangle to
       correct from: it goes straight to the slot's, and the next frame corrects. */
    const measured = at.width > 0 || at.height > 0;
    put(host, "left", `${measured ? (parseFloat(host.style.left) || 0) + r.left - at.left : r.left}px`);
    put(host, "top", `${measured ? (parseFloat(host.style.top) || 0) + r.top - at.top : r.top}px`);
    put(host, "width", `${r.width}px`);
    if (sheet) {
      const c = sheet.getBoundingClientRect();
      const top = Math.max(0, c.top - r.top);
      const bottom = Math.max(0, r.top + h - c.bottom);
      put(host, "clipPath", top || bottom ? `inset(${top}px 0 ${bottom}px 0)` : "");
      if (h && (top >= h || bottom >= h)) put(host, "display", "none");
    } else put(host, "clipPath", "");
  }
  const onScrollOrResize = (): void => placeHost();

  /** Every frame while the host is mounted, and only then. */
  function followHost(): void {
    if (hostFrame !== null || typeof win.requestAnimationFrame !== "function") return;
    const step = (): void => {
      if (!payHost) { hostFrame = null; return; }
      placeHost();
      hostFrame = win.requestAnimationFrame(step);
    };
    hostFrame = win.requestAnimationFrame(step);
  }

  /** The screen stays awake only while the code is actually on it: not hidden,
   *  not spent, not on another route. Run on every repaint. */
  function syncWake(s: InPersonState | null): void {
    const showing = !!s && s.phase === "code" && !s.codeExpired && !s.hidden && !!win.document.querySelector(".ip-code");
    if (showing) void requestWakeLock(); else if (wakeLock) releaseWakeLock();
  }

  function mountPay(): void {
    const s = state();
    syncWake(s);
    const slot = win.document.querySelector<HTMLElement>(PAY_SLOT_SELECTOR);
    const hook = w.__gbzInPersonPay;
    /* payFailed: a failed form is mounted again only by Try again (m-2: a form unmounted mid-tap must not come back on
       the next repaint and hide Try again; a form that failed to load stays mounted, so a late ready still wins). */
    if (!s || !hook || s.phase !== "confirm" || !s.fee || s.payFailed) {
      /* E4 keeps the element (hidden, since the offline screen has no slot):
         the retry lands back on the confirm with the card still typed in it,
         and pays through the same element rather than an empty new one. */
      if (payHost && (!s || (s.phase !== "confirm" && s.phase !== "offline"))) unmountPayHost();
      else placeHost();
      return;
    }
    if (payHost && payHostGen === gen) { placeHost(); return; }
    if (!slot) return;
    unmountPayHost();
    const host = win.document.createElement("div");
    host.className = "ip-pay-host";
    host.style.position = "fixed";
    /* placeHost corrects from where the host actually is; start it at a known place */
    host.style.left = "0px";
    host.style.top = "0px";
    /* Next to #app, INSIDE the install's scope root (the live site scopes its
       styles and hides the whole root off /web), never detached by app.js's
       innerHTML repaints of #app itself. */
    const app = win.document.getElementById("app");
    if (app && app.parentNode) app.parentNode.insertBefore(host, app.nextSibling);
    else win.document.body.appendChild(host);
    payHost = host;
    payHostGen = gen;
    const RO = (win as Window & { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
    if (RO) { hostResize = new RO(() => placeHost()); (hostResize as ResizeObserver).observe(host); }
    const g = gen;
    unmountPay = hook.mount(host, {
      feeCents: s.feeCents,
      intent: async () => {
        const cur = state();
        if (!live(g) || !cur || !cur.offerId) throw new Error(FAILURE_MESSAGE);
        if (cur.clientSecret) return cur.clientSecret;
        if (!cur.second) {
          const r = await start(cur, idempotencyKey(), g);
          if (!r || !r.clientSecret) throw new Error(FAILURE_MESSAGE);
          if (cur.payment || readPayment(r).kind !== "absent") {
            /* Item mode: the element holds only the quote it was mounted with. */
            if (takeStartQuote(cur, r) !== "pay") throw new Error(amountChanged(state()?.payment?.totalCents ?? 0));
            return r.clientSecret;
          }
          if (!isScreenFee(r.feeCents)) { startedUnreadable(cur); throw new Error(AMOUNT_UNAVAILABLE); }
          mark(cur.threadId, { started: true, fee: cur.fee });
          set({ clientSecret: r.clientSecret, code: r.code, expiresAt: r.expiresAt, feeCents: r.feeCents });
          return r.clientSecret;
        }
        let fee: Awaited<ReturnType<typeof authorizeFee>>;
        try {
          fee = await authorizeFee(cur, g);
        } catch (err) {
          /* A wallet tap reaches authorize-fee from here: the same screens. */
          if (err instanceof HandoffEndedError && live(g) && !(await settledMeanwhile(cur, g)) && live(g)) {
            set({ phase: err.phase, error: err.message, busy: false });
          }
          throw err;
        }
        if (fee.changed) throw new Error(amountChanged(state()?.payment?.totalCents ?? 0));
        if (!fee.clientSecret) throw new Error(FAILURE_MESSAGE);
        return fee.clientSecret;
      },
      /* Only when something changes: every set() repaints the screen, and an
         unconditional set here would loop forever. */
      onReady: ({ wallet }) => {
        if (live(g)) clearPayFormTimer();
        const cur = state();
        if (!live(g) || !cur || (cur.payReady && cur.payWallet === wallet)) return;
        set({ payReady: true, payWallet: wallet, payMode: wallet ? "wallet" : "card", payFailed: false });
      },
      onPaid: () => {
        if (!live(g)) return;
        const cur = state();
        if (!cur || !cur.clientSecret || paidHandled.has(cur.clientSecret)) return;
        paidHandled.add(cur.clientSecret);
        const pend = pendingPaid;
        pendingPaid = null;
        if (pend && pend.secret === cur.clientSecret) { void pend.then(); return; }
        if (!cur.second) { set({ phase: "code", codeExpired: false, busy: false, payErr: null }); startClocks(); return; }
        void confirmOrRetry(idempotencyKey(), g);
      },
      onError: (message) => { if (live(g)) set({ busy: false, payErr: message }); },
      onLoadFailed: () => { if (live(g)) payFormFailed(); },
    });
    payFormTimer = setTimer(() => { payFormTimer = null; if (live(g)) payFormFailed(); }, PAY_FORM_TIMEOUT_MS);
    placeHost();
    followHost();
  }

  /* ── clicks ────────────────────────────────────────────────────────────── */

  /** "Have an event code?": redeem it, then reopen the deal from the server so
   *  every screen reads the waived quote fresh (the server releases any $1
   *  hold already taken on the deal). */
  async function applyEventCode(): Promise<void> {
    const s = state();
    if (!s || !s.offerId || s.busy) return;
    const typed = (win.prompt("Type the event code") ?? "").trim();
    if (!typed) return;
    const g = gen;
    set({ busy: true, payErr: null, confirmErr: null });
    let res: Response;
    try {
      res = await post(`/api/offers/${encodeURIComponent(s.offerId)}/${EVENT_CODE_PATH}/${encodeURIComponent(typed)}`, {}, idempotencyKey());
    } catch {
      if (live(g)) set({ busy: false, payErr: FAILURE_MESSAGE });
      return;
    }
    const body = (await res.json().catch(() => ({}))) as { error?: string; waived?: boolean };
    if (!live(g)) return;
    if (!res.ok) {
      /* The typed-code screen (second, not fromCode) shows confirmErr, every
         other confirm screen shows payErr: put the sentence where it renders. */
      const sentence = body.error ?? FAILURE_MESSAGE;
      set(s.second && !s.fromCode ? { busy: false, confirmErr: sentence } : { busy: false, payErr: sentence });
      return;
    }
    const { threadId, fromCode } = s;
    /* Which line, from the screen's quote before the reset (IP4a M3, adversarial pass 1 M-1). The server's `waived`
       is said for the CALLER (offersInPerson.ts: true only when the caller owes the flat $1), so a hybrid's buyer
       paying by card is always answered false, though the code did cover the seller's $1: how this caller pays picks
       Blake's hybrid line, never `waived`. A straight card purchase is never "by card" here: its buyer owes the item
       and the code waives nothing, so it gets the interim line. Every line goes through showLong (6 s, re-shown past
       the reopen's repaint), the interim one included (m-1). */
    const byCard = s.kind === "hybrid" && s.payment?.model === "item";
    reset();
    set(null);
    if (byCard) showLong(eventCodeAppliedByCard(s.with));
    else if (body.waived === true) showLong(EVENT_CODE_APPLIED);
    else showLong(EVENT_CODE_MARKED);
    if (fromCode) void resolveRoute();
    else if (threadId) void open(threadId);
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;
    if (target.closest(EVENT_CODE_SELECTOR)) { void applyEventCode(); return; }
    const open01 = target.closest(IN_PERSON_SELECTOR) as HTMLElement | null;
    if (open01) {
      const threadId = open01.dataset.inperson;
      if (threadId) void open(threadId);
      return;
    }
    if (target.closest(CONFIRM_SELECTOR)) {
      const s = state();
      if (!s || s.busy || s.phase !== "confirm") return;
      if (s.fee && !w.__gbzInPersonPay) { set({ payErr: PAY_NOT_READY }); return; }
      /* The element is still loading (or never will): the button is disabled
         and already says so; a tap that gets through anyway records nothing. */
      if (s.fee && !s.payReady) return;
      /* An intent already exists (a decline, then "Try another card"): pay it,
         never start or confirm a second time. */
      if (s.clientSecret && s.fee && !paidHandled.has(s.clientSecret)) { payExisting(s, s.clientSecret); return; }
      /* Round 5 (rule 15): a Confirm tapped after a refused Stop is the person's own new write, not the kept one. */
      stoppedOnE4 = false;
      set({ busy: true, payErr: null, confirmErr: null });
      void attemptConfirm(idempotencyKey(), gen);
      return;
    }
    if (target.closest(RETRY_SELECTOR)) {
      const s = state();
      if (!s || s.busy || !s.clientSecret) return;
      /* M8 (IP4b): after an unknown outcome the hold is read back before anything is confirmed again. */
      if (s.payErr === PAY_UNCONFIRMED) { void recheck(s, s.clientSecret); return; }
      payExisting(s, s.clientSecret);
      return;
    }
    if (target.closest(ANOTHER_CARD_SELECTOR)) {
      w.__gbzInPersonPay?.reset();
      set({ payErr: null, busy: false });
      return;
    }
    if (target.closest(RELOAD_PAY_SELECTOR)) {
      const s = state();
      if (!s || s.phase !== "confirm" || !s.payFailed) return;
      /* A fresh element on the next paint. stripeJs.ts and loadScript forget a failed load, so Stripe.js really
         loads again; a load still pending is waited on, not doubled. */
      unmountPayHost();
      set({ payFailed: false, payReady: false, payErr: null });
      return;
    }
    if (target.closest(REOPEN_SELECTOR)) {
      tryAgain();
      return;
    }
    if (target.closest(STOP_SELECTOR)) {
      void stop();
      return;
    }
    if (target.closest(CLEAR_SELECTOR)) {
      /* Back and the receipt's ✕ end the screen whatever it holds. */
      reset();
      return;
    }
    if (target.closest(DISMISS_SELECTOR) || BACKDROP_IDS.includes((target as HTMLElement).id)) {
      /* app.js's own handler paints the close; this clears the clocks and the
         element for a screen that is going away (a hide keeps them). */
      const s = state();
      if (s && !keepsRunning(s)) reset();
      return;
    }
  };

  /** IP4a M4's Try again on the error sheet. "open": In person again, from the server. "confirm": start refused
   *  before it recorded anything, so the confirm screen comes back for another tap, with a fresh card form. */
  function tryAgain(): void {
    const s = state();
    if (!s || s.phase !== "error" || !s.retry || s.busy) return;
    if (s.retry === "confirm") {
      set({ phase: "confirm", error: null, retry: null, busy: false, payReady: false, payFailed: false, payErr: null, confirmErr: null });
      return;
    }
    if (s.threadId) void open(s.threadId, true);
  }

  /** A screen the ✕ only hides: a confirm recorded on the server, a held
   *  intent, a write still in flight or still trying to reach the server. An
   *  ended screen (stopped, done, an error) is cleared whatever it holds. */
  function keepsRunning(s: InPersonState): boolean {
    if (s.busy) return true;
    if (s.phase === "code" || s.phase === "offline") return true;
    return s.phase === "confirm" && (!!s.code || !!s.clientSecret) && !s.fromCode;
  }

  /* ── install ───────────────────────────────────────────────────────────── */

  w.__gbzInPersonOpen = (threadId: string) => { void open(threadId); };
  w.__gbzInPersonQrSvg = qrSvg;
  w.__gbzInPersonKeepsRunning = (s: InPersonState) => keepsRunning(s);
  /* View Receipt's line for a deal this tab saw complete in person (IP4a M7, app.js renderReceiptModal). */
  w.__gbzInPersonCompleted = (offerId: string) => !!offerId && doneList().some((e) => e.offerId === offerId);
  win.document.addEventListener("click", onClick, true);
  win.addEventListener("hashchange", onHashChange);
  win.document.addEventListener("visibilitychange", onVisibility);
  /* capture, on the document: the sheet's own scroll does not bubble */
  win.document.addEventListener("scroll", onScrollOrResize, { capture: true, passive: true });
  win.addEventListener("resize", onScrollOrResize);
  /* iOS's keyboard moves and shrinks only the visual viewport: no window resize */
  win.visualViewport?.addEventListener("resize", onScrollOrResize);
  win.visualViewport?.addEventListener("scroll", onScrollOrResize);

  const app = win.document.getElementById("app");
  const observer = app ? new MutationObserver(() => { mountPay(); reshowLong(); }) : null;
  observer?.observe(app!, { childList: true, subtree: true });

  /* E1's return: the signed-out scanner signed in, the page reloaded, and the
     key says where they were going, and when. Once, then cleared; a key older
     than RETURN_TTL_MS (a tab left open) is dropped rather than followed to a
     code that is long spent. */
  let returned: string | null = null;
  try {
    returned = win.sessionStorage.getItem(RETURN_KEY);
    if (returned) win.sessionStorage.removeItem(RETURN_KEY);
  } catch { returned = null; }
  const back = parseReturn(returned, now());
  if (back) win.location.hash = back;
  void resolveRoute();

  return () => {
    win.document.removeEventListener("click", onClick, true);
    win.removeEventListener("hashchange", onHashChange);
    win.document.removeEventListener("visibilitychange", onVisibility);
    win.document.removeEventListener("scroll", onScrollOrResize, true);
    win.removeEventListener("resize", onScrollOrResize);
    win.visualViewport?.removeEventListener("resize", onScrollOrResize);
    win.visualViewport?.removeEventListener("scroll", onScrollOrResize);
    observer?.disconnect();
    reset();
    pendingPaid = null;
    delete w.__gbzInPersonOpen;
    delete w.__gbzInPersonQrSvg;
    delete w.__gbzInPersonKeepsRunning;
    delete w.__gbzInPersonCompleted;
  };
}
